/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import {
  createSelector,
  createSelectorCreator,
  defaultMemoize,
} from "reselect";
import {
  subDays,
  startOfDay,
  subHours,
  differenceInCalendarDays,
  differenceInMilliseconds,
} from "date-fns";
import { scaleTime, scaleLinear } from "d3-scale";
import { extent } from "d3-array";
import { zoomIdentity } from "d3-zoom";
import { xor, groupBy, orderBy, sortBy } from "lodash/fp";
import {
  Machine,
  LicenceDetails,
  TimeSeriesValueCompactValue,
  MachineType,
  getMachineTypeAsString,
  OffsetAdjustmentApplied,
  MasteringUpdate,
  OffsetChangeDetected,
  units,
  MachineConfiguration,
  GCodeFileDropMachineConfiguration,
  DefaultTimeSeriesPrecision,
  CappedTimeSeriesPrecision,
  GUID,
  UnitDisplayHintOverride,
  TimeSeriesLimit,
  GCodeFileDropMachineConfigurationDetails,
  ProcessAction,
  CommsType,
  stringFunctions,
} from "@centralwebteam/narwhal";
import { assertUnreachable } from "@/@types/assertUnreachable";
import { notUndefined } from "@/@types/guards/notUndefined";
import { RootState } from ".";
import { initialFilterState } from "./reducer";
import {
  createMetadataFilterSet,
  isEventInJobTimeRange,
  MetadataEvent,
} from "./metadataFilterUtils";
import {
  filterMachinesAndLocationsByMachineReferences,
  getMachineIdsAndNamesUsedByPage,
} from "./machineFilterUtils";
import { mapRouteNameToPath, Routes } from "./session/routeDefinitions";
import { isIsoDate } from "@/modules/string";
import { selectLastFetchedForRefresh } from "./machineAnalysis/selector";
import { sortWorstToBest } from "@/presentation/MeasurementCharacteristic/sortWorstToBest";
import { Verdict, JobPresentation } from "@/presentation/Job";
import {
  filterJobsByJobType,
  filterJobsByVerdict,
  filterJobsByStatus,
  filterJobsByMetaData,
  filterJobsByJobName,
} from "./jobFilterUtils";
import {
  KnownStartValues,
  knownStartValues,
  RefreshMode,
  AlertTypeReason,
  dataFetchedType,
  DataFetchedType,
  FocusedOrLatestJobViewMeasurementType,
  MeasurementTypeDetail,
  Filter,
} from "./state";
import { AlertState } from "@/presentation/Alert/state";
import {
  createUniqueMeasurementID,
  MeasurementCharacteristicPresentation,
} from "@/presentation/MeasurementCharacteristic";
import { Series } from "@/components/SeriesList/SeriesItem";
import { calculateUptime } from "@/modules/mtbf";
import {
  fixedTimespanFormat,
  nextMillisecondISOString,
} from "@/modules/dateFormats";
import { t } from "@/modules/string/translate";
import { ProcessActionPresentation } from "../presentation/ProcessAction/index";
import { removeUndefinedProps } from "@/modules/removeUndefinedProps";
import { sortMeasurements } from "@/modules/measurementSorting/measurements";
import { sortStatistics } from "@/modules/measurementSorting/statistics";
import { toMeasurementRowOrder } from "@/modules/measurementSorting/rowOrder";
import memo from "memoize-one";
import { SeriesCompactValuePresentation } from "@/presentation/SeriesCompactValuePresentation";
import {
  AlertLevel,
  emptyStateText,
  numberOrUndefined,
  tidyNumber,
  TreeNode,
} from "@centralwebteam/jellyfish";
import constants from "@/constants";
import { matchSampleRate } from "@/modules/sampleRate";

type hasID = { id: string };

const DEFAULT_DISPLAY_HINTS_FOR_TIMESERIES = {
  showOnCurrentStatus: false,
  sampleRate: "Default" as const,
  displayPrecision: 4,
};

/** Creates a memoized filter function. */
export function getFilteredResourceByKey(key: string) {
  return memo(function <T extends { [key: string]: any }>(
    resource: T[],
    filters: string[]
  ): T[] {
    if (!filters || !filters.length) return resource;
    return resource.filter((r) => {
      return filters.some((filter) => r[key] === filter);
    });
  }) as <T>(resource: T[], filters: string[]) => T[];
}

/**
 * A SelectorCreator for better caching of arrays of objects with IDs.
 * In place of the usual basic equality comparison, we check that the IDs of the objects in arrays a and b match.
 */
const createArrayIDsEqualSelector = createSelectorCreator(
  defaultMemoize,
  (a: hasID[], b: hasID[]) =>
    a.length === b.length &&
    a.every((ax) => b.some((bx) => bx.id === ax.id)) &&
    b.every((bx) => a.some((ax) => ax.id === bx.id))
);

// Selects all locations. Copied from the main selectors file because Webpack has problems with the import
export const selectAllLocations = createSelector(
  [(state: RootState) => state.global.locations],
  (locations) => {
    if (!locations?.length) return [];
    const ids = locations.map((l) => l.id);
    return locations.map((l) =>
      l.parentId && ids.includes(l.parentId) ? l : { ...l, parentId: undefined }
    );
  }
);

export const selectMachines = (state: RootState) => state.global.machines;

/** A location filter id covers a machine if it's the machine's own location, or an ancestor of it
 * (via that location's precomputed `descendants`), so selecting a parent location also matches
 * machines nested under its sub-locations. */
export const machineMatchesLocationOrMachineFilter = (
  machine: Machine,
  filters: Pick<Filter, "locationId" | "machineId">,
  locations: { id: string; descendants: string[] }[]
) =>
  (!filters.locationId.length && !filters.machineId.length) ||
  filters.locationId.some(
    (id) =>
      id === machine.locationId ||
      locations
        .find((l) => l.id === id)
        ?.descendants.includes(machine.locationId ?? "")
  ) ||
  filters.machineId.includes(machine.id);

/** Selects all Machines, stripped of their frequently-changing properties such as secondsSinceLastContact.
 * Returned Machines have only these properties:
        id,
        name,
        type,
        make,
        model,
        serialNumber,
        isLicensed,
        ipAddress,
        locationId.
*/
export const selectMachinesInvariant = createArrayIDsEqualSelector(
  [selectMachines],
  (machines: Machine[]) =>
    machines.map((m) => {
      const {
        id,
        name,
        type,
        make,
        model,
        serialNumber,
        isLicensed,
        ipAddress,
        locationId,
      } = m;
      return {
        id,
        name,
        type,
        make,
        model,
        serialNumber,
        isLicensed,
        ipAddress,
        locationId,
      } as Machine;
    })
);

/** Selects all locations. See also store/filter/selectors for the filtered version.
 * The raw location list may contain locations that are not reachable from the root node, because parent locations are not visible to the current user.
 * This selector places locations without accessible parents at the root level by setting their parentId to undefined.
 */
export const selectLocations = createSelector(
  [(state: RootState) => state.global.locations],
  (locations) => {
    if (!locations?.length) return [];
    const ids = locations.map((l) => l.id);
    return locations.map((l) =>
      l.parentId && ids.includes(l.parentId) ? l : { ...l, parentId: undefined }
    );
  }
);
export const selectSearchFilterDetails = (s: RootState) => s.filter.search;

export const selectFilteredLocationOptions = createSelector(
  [selectSearchFilterDetails, selectLocations],
  (FilterDetails, locations) => {
    if (FilterDetails.value === "") {
      return locations;
    }

    const key = FilterDetails.value.toLowerCase();
    const locationMap = Object.fromEntries(locations.map((l) => [l.id, l]));

    // Helper to collect all parents recursively
    function collectParents(
      location: { id: string; parentId?: string | null },
      collected = new Set<string>()
    ) {
      if (!location || collected.has(location.id)) return collected;
      collected.add(location.id);
      if (location.parentId && locationMap[location.parentId]) {
        collectParents(locationMap[location.parentId], collected);
      }
      return collected;
    }

    // Find all locations matching the key
    const matchedIds = new Set<string>();
    locations.forEach((loc) => {
      if (loc.name && loc.name.toLowerCase().includes(key)) {
        collectParents(loc, matchedIds);
      }
    });

    // Return all locations whose id is in matchedIds
    return locations.filter((loc) => matchedIds.has(loc.id));
  }
);

/** Tree node for the Machine filter category. Locations and machines share the same node shape;
 * `isMachine` distinguishes an individual machine leaf from a location, which also carries its
 * `type`/`model` so the filter can render a machine icon. */
export type MachineTreeNode = Omit<TreeNode, "children"> & {
  isMachine?: boolean;
  type?: MachineType;
  model?: string;
  children?: MachineTreeNode[];
};

/** Builds the combined location + machine tree. When `visibleLocationIds`/`visibleMachineIds` are
 * null, every location/machine is included (the full, unfiltered-by-search tree); otherwise only
 * ids present in the given sets are included. Machines are nested under their location; machines
 * without a visible location sit under the "" (unassigned) bucket. */
function buildMachineTreeNodes(
  locations: { id: string; name: string; parentId?: string | null }[],
  machines: Machine[],
  locationMap: Record<
    string,
    { id: string; name: string; parentId?: string | null }
  >,
  visibleLocationIds: Set<string> | null,
  visibleMachineIds: Set<string> | null
): MachineTreeNode[] {
  const unassignedNode: MachineTreeNode = {
    id: constants.unassignedMachinesLocation.name,
    name: constants.unassignedMachinesLocation.name,
  };
  const locationNodes: MachineTreeNode[] = locations
    .filter(
      (l) =>
        l.id !== constants.unassignedMachinesLocation.id &&
        (!visibleLocationIds || visibleLocationIds.has(l.id))
    )
    .map((l) => ({
      id: l.id,
      name: l.name,
      parentId: l.parentId ?? undefined,
    }));
  const machineNodes: MachineTreeNode[] = machines
    .filter((m) => !visibleMachineIds || visibleMachineIds.has(m.id))
    .map((m) => ({
      id: m.id,
      name: m.name,
      parentId:
        m.locationId && locationMap[m.locationId]
          ? m.locationId
          : unassignedNode.id,
      isMachine: true,
      type: m.type,
      model: m.model,
    }));

  const allNodes = [unassignedNode, ...locationNodes, ...machineNodes];
  const nodeMap = new Map(allNodes.map((n) => [n.id, n]));
  allNodes.forEach((n) => {
    if (n.parentId != null && nodeMap.has(n.parentId)) {
      const parent = nodeMap.get(n.parentId)!;
      parent.children = [...(parent.children ?? []), n];
    }
  });
  return allNodes.filter(
    (node) => node !== unassignedNode || !!node.children?.length
  );
}

/** The full combined location + machine tree, ignoring the global filter search text. Used where
 * selection completeness (e.g. collapsing to a parent location id) must be judged against every
 * descendant, not just those currently visible in a search-narrowed view. */
export const selectMachineTreeNodes = createSelector(
  [selectLocations, selectMachines],
  (locations, machines): MachineTreeNode[] => {
    const locationMap = Object.fromEntries(locations.map((l) => [l.id, l]));
    return buildMachineTreeNodes(locations, machines, locationMap, null, null);
  }
);

/** Machines referenced by the current page's data, or null when the page shows every machine. */
export const selectMachineIdsAndNamesUsedByPage = createSelector(
  [
    (s: RootState) => s.view,
    (s: RootState) => s.jobPerformance.jobTypes,
    (s: RootState) => s.processUpdates.processActions,
  ],
  (view, jobTypes, processActions) =>
    getMachineIdsAndNamesUsedByPage(view, {
      jobTypes,
      processActions,
    })
);

const selectAvailableMachinesAndLocationsForFilter = createSelector(
  [selectLocations, selectMachines, selectMachineIdsAndNamesUsedByPage],
  (locations, machines, associations) =>
    filterMachinesAndLocationsByMachineReferences(
      locations,
      machines,
      associations
    )
);

/** Builds the page-scoped location + machine tree for the Machine filter category, filtered by the
 * global filter search text. Machines are nested under their location; machines without a visible
 * location sit under the "" (unassigned) bucket. Matching a name keeps its ancestor locations visible. */
export const selectFilteredMachineTreeNodes = createSelector(
  [selectSearchFilterDetails, selectAvailableMachinesAndLocationsForFilter],
  (filterDetails, { locations, machines }): MachineTreeNode[] => {
    const key = filterDetails.value.toLowerCase();
    const locationMap = Object.fromEntries(locations.map((l) => [l.id, l]));

    function collectLocationAncestors(
      location: { id: string; parentId?: string | null } | undefined,
      collected: Set<string>
    ) {
      if (!location || collected.has(location.id)) return;
      collected.add(location.id);
      if (location.parentId && locationMap[location.parentId]) {
        collectLocationAncestors(locationMap[location.parentId], collected);
      }
    }

    function collectLocationDescendants(
      locationId: string,
      collected: Set<string>
    ) {
      locations.forEach((location) => {
        if (location.parentId === locationId && !collected.has(location.id)) {
          collected.add(location.id);
          collectLocationDescendants(location.id, collected);
        }
      });
    }

    const visibleLocationIds = new Set<string>();
    const visibleMachineIds = new Set<string>();
    // Locations matched by name (or shown as an ancestor of one); their machines are all visible.
    const locationMatchIds = new Set<string>();

    if (key === "") {
      locations.forEach((l) => visibleLocationIds.add(l.id));
      machines.forEach((m) => visibleMachineIds.add(m.id));
    } else {
      locations.forEach((l) => {
        if (l.name?.toLowerCase().includes(key)) {
          collectLocationAncestors(l, visibleLocationIds);
          locationMatchIds.add(l.id);
          collectLocationDescendants(l.id, locationMatchIds);
        }
      });
      locationMatchIds.forEach((id) => visibleLocationIds.add(id));

      machines.forEach((m) => {
        if (m.name?.toLowerCase().includes(key)) {
          visibleMachineIds.add(m.id);
          collectLocationAncestors(
            m.locationId ? locationMap[m.locationId] : undefined,
            visibleLocationIds
          );
        }
      });

      // Only include a machine via its location when that location matched the search by name,
      // not merely because it's an ancestor path shown for a different matching machine.
      machines.forEach((m) => {
        if (m.locationId && locationMatchIds.has(m.locationId)) {
          visibleMachineIds.add(m.id);
        }
      });
    }

    return buildMachineTreeNodes(
      locations,
      machines,
      locationMap,
      visibleLocationIds,
      visibleMachineIds
    );
  }
);

/** All machine names reachable from the given set of selected location and machine ids */
export const selectAllowedMachineNamesForMachineFilter = createSelector(
  [
    (s: RootState) => s.filter.active.locationId,
    (s: RootState) => s.filter.active.machineId,
    selectMachines,
    selectLocations,
  ],
  (locationIds, machineIds, machines, locations): Set<string> | null => {
    if (!locationIds.length && !machineIds.length) return null;
    const names = new Set<string>();
    machines.forEach((m) => {
      if (
        machineMatchesLocationOrMachineFilter(
          m,
          { locationId: locationIds, machineId: machineIds },
          locations
        )
      ) {
        names.add(m.name);
      }
    });
    return names;
  }
);

export const selectIsAuthenticated = (state: RootState) => state.authenticated;

/** Returns true if we are currently checking either the server licence or the user credentials */
export const selectIsAuthenticating = (state: RootState) =>
  state.authenticating;

export const selectUsername = (state: RootState) => state.username;

export const selectUserSystemPermissions = (state: RootState) =>
  state.systemPermissions;

export const selectPath = createSelector(
  (state: RootState) => state.view,
  (state: RootState) => state.params as any,
  (current, params): string => {
    return `${mapRouteNameToPath[(current as Routes) ?? "current status"].stringify()}?${
      // only display the known params (by key) and those which have values
      Object.keys(params)
        .filter((key) => params[key] !== null && params[key] !== "")
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    }`;
  }
);

export const selectCurrentRoute = (state: RootState) => state.view;

/**
 * This will return raw values so end is possibly undefined and start a token.
 */
export const selectStartAndEndRaw = ({ params: { start, end } }: RootState) =>
  [start, end] as const;

/**
 * IMPORTANT - This will always return actual Dates (as ISOString) and never the start token!
 */
export const selectStartAndEnd = ({ params: { start, end } }: RootState) => [
  isValidStartToken(start) ? getStartDate(start).toISOString() : start,
  end ?? new Date().toISOString(),
];

/**
 * IMPORTANT - This will always return actual Dates and never the start token!
 */
export const selectStartAndEndDates = createSelector(
  (state: RootState) => state.params.start,
  (state: RootState) => state.params.end,
  (start, end): [Date, Date] =>
    isValidStartToken(start)
      ? [getStartDate(start), new Date()]
      : dates(start, end)
);

const dates = (start: string, end: string | null): [Date, Date] => [
  new Date(start),
  end ? new Date(end) : new Date(),
];

export const selectFocusedMachine = createSelector(
  (state: RootState) => state.global.machines,
  (state: RootState) => state.params.focusedMachineId,
  (machines, focusedMachineId): Machine | undefined =>
    focusedMachineId
      ? machines.find(({ id }) => id === focusedMachineId)
      : undefined
);
export const selectFocusedMachineStateSummary = (s: RootState) =>
  s.focusedMachine.stateSummary;

export const selectFocusedJobId = (state: RootState) =>
  state.params.focusedJobId;

export const selectIsRollingMode = createSelector(
  (state: RootState) => state.params.start,
  (start): boolean => {
    return isValidStartToken(start);
  }
);

export const selectLastFetchCompletedMachinePerformance = (state: RootState) =>
  state.machinePerformance.lastFetchCompleted;

/**
 * Could be a string token and not a date string!
 */
export const selectRawStart = (state: RootState) => state.params.start;

export const selectPreviousLiveModeToken = (state: RootState) =>
  state.previousLiveModeToken;

/**
 * Gets the start date for live mode.
 */
export function getStartDate(token: KnownStartValues) {
  const now = new Date();
  switch (token) {
    case "1 hour": {
      return subHours(now, 1);
    }
    case "1 day": {
      return startOfDay(subDays(now, 1));
    }
    case "3 days": {
      return startOfDay(subDays(now, 3));
    }
    case "7 days": {
      return startOfDay(subDays(now, 7));
    }
    default: {
      assertUnreachable(token);
    }
  }
}

export function isValidStartToken(token: string): token is KnownStartValues {
  return knownStartValues.includes(token as KnownStartValues);
}
export const selectPasswordResetToken = createSelector(
  (state: RootState) => state.params.token,
  (token) => (!token || token === "" ? undefined : token)
);

const validateStartValue = (start: string | null) =>
  start === null
    ? "invalid"
    : isIsoDate(start)
      ? "date iso string"
      : isValidStartToken(start)
        ? "start token"
        : "invalid";
const validateEndValue = (end: string | null) =>
  end === null ? "invalid" : isIsoDate(end) ? "date iso string" : "invalid";

/**
 * Correctly handles many different start/end value combinations and returns either [token, null] (live mode) or [date string, date string] (sticky mode)
 * @param start
 * @param end
 */
export const getValidStartAndEndValues = (
  start: string | null,
  end: string | null
): [KnownStartValues, null] | [string, string] => {
  const startValueType = validateStartValue(start);
  const endValueType = validateEndValue(end);
  return startValueType === "date iso string" &&
    endValueType === "date iso string"
    ? [start! < end! ? start! : end!, end! > start! ? end! : start!]
    : startValueType === "date iso string" && endValueType === "invalid"
      ? [start!, new Date().toISOString()]
      : startValueType === "invalid" && endValueType === "date iso string"
        ? [subDays(new Date(end!), 1).toISOString(), end!]
        : startValueType === "start token"
          ? [start as KnownStartValues, null]
          : [knownStartValues[0], null];
};

export const getValidStartValue = (
  start: string,
  end: string | null
): string => {
  const daysDifference = daysDifferenceFromNow(start);
  if (end === null) {
    if (daysDifference <= 1) {
      return "1 day";
    } else if (daysDifference > 1 && daysDifference <= 3) {
      return "3 days";
    } else {
      return "7 days";
    }
  } else if (end !== null && daysDifference <= 1) {
    return "1 day";
  } else {
    return startOfDay(new Date(start)).toString();
  }
};

const daysDifferenceFromNow = (start: string): number => {
  const now = new Date();
  return differenceInCalendarDays(now, new Date(start));
};

export const selectMachineSelectorState = (s: RootState) => s.machineSelector;

export const selectFocusedMachineJobTypeFilter = (s: RootState) =>
  s.focusedMachine.filters.jobType;

export const selectFocusedMachineSelectedAlerts = (s: RootState) =>
  s.focusedMachine.selectedAlerts;

export const selectFocusedMachineParetoSelectedReason = (s: RootState) =>
  s.focusedMachine.alertTypeParetoSelectedReason;

export const selectFocusedMachineJobs = createSelector(
  [(s: RootState) => s.focusedMachine.jobs],
  (jobs: Mappy<JobPresentation>) =>
    Object.values(jobs).filter(notUndefined) as JobPresentation[]
);

// Builds TreeNode of visible job metadata grouped by name and value.
// Used by UI trees and metadata filtering.
export const selectFocusedMachineJobMetadataAsTreeNodes = createSelector(
  [(s: RootState) => s.focusedMachine.jobMetadata],
  (jobMetadata): TreeNode[] => {
    // Group metadata by name, collecting unique values
    const metadataGroups = new Map<string, Set<string>>();

    jobMetadata.forEach((item: any) => {
      if (
        item.subType?.toLowerCase() === "metadata" &&
        item.name &&
        item.value !== undefined &&
        item.value !== null &&
        item.displayHints?.userVisible
      ) {
        if (!metadataGroups.has(item.name)) {
          metadataGroups.set(item.name, new Set<string>());
        }
        metadataGroups.get(item.name)!.add(String(item.value));
      }
    });
    const nodes: TreeNode[] = [];
    metadataGroups.forEach((values, metadataName) => {
      // Parent node
      nodes.push({
        id: metadataName,
        name: metadataName,
        parentId: undefined,
        children: Array.from(values)
          .sort()
          .map((value) => ({
            id: `${value}`,
            name: value,
            parentId: `${metadataName}`,
          })),
      });

      // Child nodes
      Array.from(values)
        .sort()
        .forEach((value) => {
          nodes.push({
            id: `${value}`,
            name: value,
            parentId: metadataName,
          });
        });
    });

    return nodes.sort((a, b) => {
      if (!a.parentId && !b.parentId) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });
  }
);

export const selectFocusedMachineJobMetadataSelectionTypes = createSelector(
  [
    selectFocusedMachineJobMetadataAsTreeNodes,
    (s: RootState) => s.userViewPreference.filters.jobFilter.draft,
  ],
  (
    nodes: TreeNode[],
    draft
  ): Record<string, "none" | "all" | "inner" | "outer"> => {
    const selection: Record<string, "none" | "all" | "inner" | "outer"> = {};

    const selectedIds = new Set<string>();
    if (draft?.metaData) {
      Object.entries(draft.metaData).forEach(([_, values]: any) => {
        (values || []).forEach((v: string) => selectedIds.add(String(v)));
      });
    }

    nodes.forEach((node) => {
      if (node.parentId) {
        // child node
        selection[node.id] = selectedIds.has(node.id) ? "all" : "none";
      } else {
        // parent node
        const children = node.children || [];
        if (children.length === 0) {
          selection[node.id] = "none";
        } else {
          const selectedChildren = children.filter((c) =>
            selectedIds.has(c.id)
          );
          if (selectedChildren.length === 0) selection[node.id] = "none";
          else if (selectedChildren.length === children.length)
            selection[node.id] = "all";
          else selection[node.id] = "inner";
        }
      }
    });

    return selection;
  }
);

const passVerdicts: Verdict[] = ["Pass", "Warning"];
const failVerdicts: Verdict[] = ["Fail"];

export type JobTypeSummary = {
  name: string;
  passes: number;
  fails: number;
  noVerdicts: number;
  completed: number;
  incomplete: number;
};
export const selectJobTypeSummaries = createSelector(
  [selectFocusedMachineJobs],
  (jobs): JobTypeSummary[] =>
    Object.values(
      jobs.reduce<{
        [name: string]: JobTypeSummary;
      }>((summaries, job) => {
        // figure out if we have an existing record
        const existingRecord = summaries[job.name];
        const verdictType = passVerdicts.includes(job.verdict)
          ? "pass"
          : failVerdicts.includes(job.verdict)
            ? "fail"
            : "no verdict";
        return existingRecord
          ? {
              ...summaries,
              [job.name]: {
                ...existingRecord,
                // record exists so increment
                passes:
                  existingRecord.passes + (verdictType === "pass" ? 1 : 0),
                fails: existingRecord.fails + (verdictType === "fail" ? 1 : 0),
                noVerdicts:
                  existingRecord.noVerdicts +
                  (verdictType === "no verdict" ? 1 : 0),
                completed:
                  existingRecord.completed +
                  (job.status.toLowerCase() === "completed" ? 1 : 0),
                incomplete:
                  existingRecord.incomplete +
                  (job.status.toLowerCase() !== "completed" ? 1 : 0),
              },
            }
          : {
              ...summaries,
              // create new record
              [job.name]: {
                name: job.name,
                passes: verdictType === "pass" ? 1 : 0,
                fails: verdictType === "fail" ? 1 : 0,
                noVerdicts: verdictType === "no verdict" ? 1 : 0,
                completed: job.status.toLowerCase() === "completed" ? 1 : 0,
                incomplete: job.status.toLowerCase() !== "completed" ? 1 : 0,
              },
            };
      }, {})
    )
);

export const selectActiveMeasurementDetails = createSelector(
  [(s: RootState) => s.focusedMachine.measurementDetails],
  (measurements) =>
    Object.values(measurements)
      .filter(notUndefined)
      .filter((measurement) => measurement.active)
);

export const selectActiveMeasurementDetailsWithoutData = createSelector(
  [
    selectActiveMeasurementDetails,
    (s: RootState) => s.focusedMachine.measurementValues,
  ],
  (selectActiveMeasurementTypes, values) =>
    selectActiveMeasurementTypes.filter(
      (type) => values[createUniqueMeasurementID(type)] === undefined
    )
);

export const selectStates = createSelector(
  [(s: RootState) => s.focusedMachine.states],
  (states) =>
    Object.values(states)
      .filter(notUndefined)
      .sort((a, b) => a.created.localeCompare(b.created))
);

export const selectAlerts = createSelector(
  [(s: RootState) => s.focusedMachine.alerts],
  (alerts) =>
    Object.values(alerts)
      .filter(notUndefined)
      .filter((a) => a.state === AlertState.set)
);

export const selectProcessActions = createSelector(
  [(s: RootState) => s.focusedMachine.processActions],
  (pa) =>
    (
      Object.values(pa).filter(notUndefined) as (
        | OffsetAdjustmentApplied
        | MasteringUpdate
        | OffsetChangeDetected
      )[]
    ).map((pa) => new ProcessActionPresentation(pa))
);

function filterByJob(processAction: ProcessAction, jobs: string[]) {
  // If the PA does not have a job name, remove from filtering rules
  if (!processAction.details?.processTrigger?.jobName) {
    return true;
  }

  return jobs.length
    ? jobs.includes(processAction.details?.processTrigger?.jobName ?? "")
    : true;
}

function filterMasteringByJob(processAction: MasteringUpdate, jobs: string[]) {
  return jobs.length ? jobs.includes(processAction.masterJobName ?? "") : true;
}

function filterByFeatureName(
  processAction: ProcessAction,
  activeMeasurementTypes: MeasurementTypeDetail[]
) {
  return activeMeasurementTypes.length
    ? activeMeasurementTypes.some(
        (measurement) =>
          measurement.featureName ===
          processAction.details?.processTrigger?.characteristic?.featureName
      )
    : true;
}

function filterByCharacteristicName(
  processAction: ProcessAction,
  activeMeasurementTypes: MeasurementTypeDetail[]
) {
  return activeMeasurementTypes.length
    ? activeMeasurementTypes.some(
        (measurement) =>
          measurement.name ===
          processAction.details?.processTrigger?.characteristic?.name
      )
    : true;
}

// Filter process actions by job ID from analysisTrigger.
function filterProcessActionsByFilteredJobs(
  processAction: ProcessAction | MasteringUpdate,
  filteredJobIds: Set<GUID>
): boolean {
  if (filteredJobIds.size === 0) return true;

  const jobId = processAction.details?.analysisTrigger?.id;

  // If the PA does not have a job ID, remove from filtering rules
  if (!jobId) return true;

  return filteredJobIds.has(jobId);
}

export const selectActiveJobTypes = (state: RootState) =>
  state.focusedMachine.filters.jobType;

export const selectJobMetadataStatus = (s: RootState) =>
  s.focusedMachine.jobMetadataStatus;

export const selectActiveJobFilters = (state: RootState) =>
  state.userViewPreference.filters.jobFilter.active;

/** Select jobs to show on the timeline (filtered by job type selection) */
export const selectFilteredJobs = createSelector(
  [
    selectFocusedMachineJobs,
    selectActiveJobTypes,
    selectActiveJobFilters,
    (s: RootState) => s.focusedMachine.jobMetadata,
  ],
  (jobs, filters, jobFilter, jobMetadata) => {
    let filtered = filterJobsByJobType(jobs, filters);
    filtered = filterJobsByJobName(filtered, jobFilter.jobName);
    filtered = filterJobsByVerdict(filtered, jobFilter.verdict);
    filtered = filterJobsByStatus(filtered, jobFilter.status);
    filtered = filterJobsByMetaData(filtered, jobFilter.metaData, jobMetadata);
    return filtered;
  }
);

export const selectProcessActionsByFilteredJobAndActiveMeasurementTypes =
  createSelector(
    [
      (s: RootState) => s.focusedMachine.processActions,
      selectFocusedMachineJobTypeFilter,
      selectActiveMeasurementDetails,
      (s: RootState) => s.userViewPreference.filters.jobFilter.active,
      selectFilteredJobs,
    ],
    (
      allProcessActions,
      filteredJobs,
      activeMeasurementTypes,
      activeJobFilters,
      filteredJobsList
    ) => {
      const definedProcessActions =
        Object.values(allProcessActions).filter(notUndefined);

      // Create a Set of filtered job IDs from the filtered jobs list
      const filteredJobIds = new Set<GUID>(
        filteredJobsList
          .filter(
            (job) =>
              !activeJobFilters.jobName?.length ||
              activeJobFilters.jobName.includes(job.name)
          )
          .filter((job) => filteredJobs.includes(job.name))
          .map((job) => job.id)
      );

      const processActions = definedProcessActions.filter(
        (processAction) =>
          processAction.subType !== "MasteringUpdate" &&
          filterByJob(processAction, filteredJobs) &&
          filterByCharacteristicName(processAction, activeMeasurementTypes) &&
          filterByFeatureName(processAction, activeMeasurementTypes) &&
          filterProcessActionsByFilteredJobs(processAction, filteredJobIds)
      );

      const masteringActions = definedProcessActions.filter(
        (processAction) =>
          processAction.subType === "MasteringUpdate" &&
          filterMasteringByJob(
            processAction as MasteringUpdate,
            filteredJobs
          ) &&
          filterProcessActionsByFilteredJobs(processAction, filteredJobIds)
      );

      const filteredActions = orderBy(
        ["created"],
        ["asc"],
        [...processActions, ...masteringActions]
      );
      return filteredActions.map(
        (pa: ProcessAction | MasteringUpdate) =>
          new ProcessActionPresentation(pa)
      );
    }
  );

const selectAlertsByLevel = createSelector(
  [(s: RootState) => s.filter.active.alertLevel, selectAlerts],
  (activeAlertLevel, alerts) =>
    getFilteredResourceByKey("level")(alerts, activeAlertLevel)
);

// Alert levels which are subtypes of AlertLevel.offset
const offsetLevels = [
  AlertLevel.offsetError,
  AlertLevel.offsetInfo,
  AlertLevel.offsetWarning,
  AlertLevel.offsetOther,
  AlertLevel.offsetOtherInfo,
];

export const selectProcessActionsByLevel = createSelector(
  [
    (s: RootState) => s.filter.active.alertLevel as AlertLevel[],
    selectProcessActionsByFilteredJobAndActiveMeasurementTypes,
  ],
  (activeAlertLevels, processActions) => {
    const levels = activeAlertLevels.includes(AlertLevel.offset)
      ? [...activeAlertLevels, ...offsetLevels]
      : activeAlertLevels.filter((level) => !offsetLevels.includes(level));

    return levels.length === 0
      ? []
      : getFilteredResourceByKey("level")(processActions, levels);
  }
);

export const selectFilteredAlertNames = (state: RootState) =>
  state.focusedMachine.filters.alertName;

/** Filtered AlertPresentations and ProcessActionPresentations for the focused machine */
export const selectEventsToDisplay = createSelector(
  [selectAlertsByLevel, selectProcessActionsByLevel, selectFilteredAlertNames],
  (alerts, processActions, filteredAlertNames) => {
    const allAlerts = orderBy(
      ["created"],
      ["asc"],
      [...alerts, ...processActions]
    );
    return allAlerts.filter(
      (e) => !filteredAlertNames?.length || filteredAlertNames.includes(e.name)
    );
  }
);

export const selectTimelineWidth = (s: RootState) =>
  s.visualisations.machineAnalysis.dimensions.timeline.width;

export const selectTimelineTransform = (s: RootState) =>
  s.visualisations.machineAnalysis.transforms.timeline;

export const selectIsTimeLineFilterPanelOpen = (s: RootState) =>
  s.visualisations.machineAnalysis.isTimeLineFilterPanelOpen;

export const selectSeriesChartClickedCoordinates = (s: RootState) =>
  s.visualisations.machineAnalysis.seriesChart.clickedCoordinates;

export const selectSelectedTimelineSeriesId = (s: RootState) =>
  s.visualisations.machineAnalysis.id;

export const selectFocusedOrLatestJobViewType = (s: RootState) =>
  s.userViewPreference.focusedOrLatestJobViewType;

export const selectIsOutOfToleranceFilterEnabled = (s: RootState) =>
  s.userViewPreference.isOutOfToleranceFilterEnabled;

export const selectFocusedOrLatestJobViewMeasurementType = (s: RootState) =>
  s.userViewPreference
    .focusedOrLatestJobViewMeasurementType as FocusedOrLatestJobViewMeasurementType;

export const selectLoginView = (s: RootState) => s.loginView;

const selectTimelineDomain = createSelector(
  [
    (s: RootState) => s.params.start,
    (s: RootState) => s.params.end,
    (s: RootState) => s.focusedMachine.lastFetched,
    selectCurrentRoute,
  ],
  (start, end, lastFetched, route): [Date, Date] => {
    if (route === "machine analysis" && isValidStartToken(start)) {
      const endDate = lastFetched
        ? new Date(lastFetched)
        : end
          ? new Date(end)
          : new Date();
      const startDate = isValidStartToken(start)
        ? getStartDate(start)
        : new Date(start);
      return [startDate, endDate];
    } else {
      const startDate = isValidStartToken(start)
        ? getStartDate(start)
        : new Date(start);
      // end should be defined but we can fallback to now incase it isn't
      return [startDate, end ? new Date(end) : new Date()];
    }
  }
);

export const selectFocusedMachineLatestJob = createSelector(
  [selectFocusedMachineJobs],
  (jobs): JobPresentation | undefined => jobs.at(-1)
);

const closestMeasurementToTime = (
  values:
    | TimeSeriesValueCompactValue[]
    | MeasurementCharacteristicPresentation[],
  time: Date,
  plotType: "actual" | "deviation"
) => {
  if (Array.isArray(values[0])) {
    const val = (values as TimeSeriesValueCompactValue[]).reduce(
      (
        a: TimeSeriesValueCompactValue | undefined,
        b: TimeSeriesValueCompactValue
      ) =>
        a &&
        Math.abs(differenceInMilliseconds(Date.parse(a[0]), time)) <
          Math.abs(differenceInMilliseconds(Date.parse(b[0]), time))
          ? a
          : b,
      undefined
    );
    return val
      ? {
          time: new Date(Date.parse(val[0])),
          value: val[1],
          toleranceType: undefined,
          verdict: "No Verdict",
          unit: val[2],
        }
      : undefined;
  } else {
    const val = (values as MeasurementCharacteristicPresentation[]).reduce(
      (
        a: MeasurementCharacteristicPresentation | undefined,
        b: MeasurementCharacteristicPresentation
      ) =>
        a &&
        Math.abs(differenceInMilliseconds(Date.parse(a.created), time)) <
          Math.abs(differenceInMilliseconds(Date.parse(b.created), time))
          ? a
          : b,
      undefined
    );
    return val
      ? {
          time: new Date(Date.parse(val.created)),
          value: plotType === "actual" ? val.actual : val.deviation,
          toleranceType: val.toleranceType,
          verdict: val.verdict,
          unit: val.unit,
        }
      : undefined;
  }
};

/** Unit conversion hints for the focused machine */
export const selectUnitHints = (s: RootState) => s.focusedMachine.hints;

/** The most recent known unit conversion hints for the focused machine */
export const selectLatestUnitHints = (s: RootState) =>
  [...s.focusedMachine.hints]
    .sort((a, b) => a.created.localeCompare(b.created))
    .at(-1);

/** From the active series, selects the points closest to time.
 * Returns details of each series with a single value, sorted by parent and name. */
export const selectActiveSeriesValuesAtTime = (time: Date) =>
  createSelector(
    [
      selectSeriesColours,
      selectActiveMeasurementTypes,
      selectActiveTimeSeriesTypes,
      selectActiveMeasurementValuesWithDisplayUnits,
      selectTimeSeriesValuesWithDisplayUnits,
      selectLatestUnitHints,
      selectMeasurementPlotType,
    ],
    (
      colours,
      msd,
      tsd,
      measurementValues,
      timeSeriesValues,
      hints,
      plotType
    ) => {
      const result: {
        id: string;
        colour?: string;
        name: string;
        /** Tolerance name or group name */
        parent?: string;
        toleranceType?: string;
        unit?: string;
        time: Date;
        value: string;
        verdict: Verdict | string;
        displayPrecision: number;
      }[] = [
        ...msd.map((m) => {
          return {
            id: createUniqueMeasurementID(m),
            ...m,
            displayUnit: getDisplayUnit(m, hints?.value, hints?.overrides),
            dataOrigin: "measurement",
          };
        }),
        ...tsd.map((t) => {
          const precision =
            t.displayHints?.displayPrecision ?? DefaultTimeSeriesPrecision;
          return {
            ...t,
            displayUnit: getDisplayUnit(t, hints?.value, hints?.overrides),
            displayPrecision: Math.min(precision, CappedTimeSeriesPrecision),
            dataOrigin: "timeseries",
          };
        }),
      ]
        .filter(notUndefined)
        .map((d) => {
          const data =
            (d.dataOrigin === "timeseries"
              ? timeSeriesValues[d.id]
              : measurementValues[d.id]
            )?.data ?? [];

          const val = closestMeasurementToTime(data, time, plotType);

          return val
            ? {
                id: d.id,
                colour: colours[d.id],
                name: d.name,
                parent:
                  // @ts-ignore type mashing
                  d.grouping || d.featureName || t`heading-Unassigned Series`,
                displayPrecision:
                  d.dataOrigin === "timeseries"
                    ? (d as any).displayPrecision
                    : DefaultTimeSeriesPrecision,
                ...val,
                value:
                  typeof val.value === "string"
                    ? val.value
                    : tidyNumber(
                        val.value,
                        d.dataOrigin === "timeseries"
                          ? (d as any).displayPrecision
                          : DefaultTimeSeriesPrecision
                      ),
                unit: val.unit ?? d.unit,
              }
            : undefined;
        })
        .filter(notUndefined)
        // Sort alphabetically by parent and name
        .sort(
          (a, b) =>
            a.parent.localeCompare(b.parent) ?? a.name.localeCompare(b.name)
        );

      return result;
    }
  );

export const selectTimelineScale = createSelector(
  [selectTimelineDomain, selectTimelineWidth, selectTimelineTransform],
  (domain, width, transform) => {
    const reducedWidth = width - constants.spaceToRenderOutofViewJobDiamonds;
    const baseScale = scaleTime().range([0, reducedWidth]).domain(domain);

    const transformedScale = zoomIdentity
      .translate(transform.x, 0)
      .scale(transform.k)
      .rescaleX(baseScale);

    // Get the transformed domain
    const transformedDomain = transformedScale.domain();

    // Clamp the domain to prevent seeing beyond the original boundaries
    // This ensures the timeline never shows time beyond lastFetched (domain[1])
    const [originalStart, originalEnd] = domain;
    const [viewStart, viewEnd] = transformedDomain;
    const viewDuration = viewEnd.getTime() - viewStart.getTime();

    // If the view extends past the original end, shift it back
    if (viewEnd > originalEnd) {
      const clampedEnd = originalEnd;
      const clampedStart = new Date(
        Math.max(originalStart.getTime(), clampedEnd.getTime() - viewDuration)
      );
      return scaleTime()
        .range([0, reducedWidth])
        .domain([clampedStart, clampedEnd]);
    }

    // If the view starts before the original start, shift it forward
    if (viewStart < originalStart) {
      const clampedStart = originalStart;
      const clampedEnd = new Date(
        Math.min(originalEnd.getTime(), clampedStart.getTime() + viewDuration)
      );
      return scaleTime()
        .range([0, reducedWidth])
        .domain([clampedStart, clampedEnd]);
    }

    return transformedScale;
  }
);

export const selectYAxisLimits = (id: string) =>
  createSelector(
    [
      (state: RootState) => selectTimeSeriesValuesPresentation(state, id),
      selectTimeSeriesLimits,
      selectTimeSeriesTypes,
      selectLatestUnitHints,
      (state: RootState) => state.focusedMachine.timeSeriesDisplayLimits[id],
    ],
    (data, limits, timeseries, hints, userDisplayLimits) => {
      if (!data) return undefined;

      let domainExtent = extent(data, (x: any) => x.value) as [number, number];

      // If user has provided display limits, use those first
      if (
        userDisplayLimits &&
        (userDisplayLimits.lowerLimit !== undefined ||
          userDisplayLimits.upperLimit !== undefined)
      ) {
        const { lowerLimit: userLower, upperLimit: userUpper } =
          userDisplayLimits;

        return [
          userLower !== undefined ? userLower : domainExtent[0],
          userUpper !== undefined ? userUpper : domainExtent[1],
        ] as [number, number];
      }

      const seriesType = timeseries.find((t: any) => t.id === id);
      let limitValues = limits[id]?.data.at(-1);

      if (seriesType && limitValues && hints) {
        const displayUnit = getDisplayUnit(
          seriesType,
          hints.value,
          hints?.overrides
        );
        const converter = getUnitConverter(
          seriesType.unitType,
          seriesType.unit,
          displayUnit
        );
        if (typeof converter === "function") {
          limitValues = convertLimits(limitValues, converter);
        }
      }

      if (limitValues) {
        if (
          limitValues.upperDisplayLimit &&
          limitValues.upperDisplayLimit > domainExtent[1]
        )
          domainExtent = [
            limitValues.lowerDisplayLimit ?? 0,
            limitValues.upperDisplayLimit,
          ];
        else if (
          limitValues.upperOperatingLimit &&
          limitValues.upperOperatingLimit > domainExtent[1]
        )
          domainExtent = [
            limitValues.lowerOperatingLimit ?? 0,
            limitValues.upperOperatingLimit,
          ];
        else if (
          limitValues.upperWarningLimit &&
          limitValues.upperWarningLimit > domainExtent[1]
        )
          domainExtent = [
            limitValues.lowerWarningLimit ?? 0,
            limitValues.upperWarningLimit,
          ];
      }

      return domainExtent;
    }
  );

/**
 * Domain is set to min of extent / lower tolerance and max of extent / upper tolerance.
 */
export const makeSelectYAxisScale = (
  id: string,
  type: "measurement" | "sensor"
) =>
  type === "measurement"
    ? createSelector(
        [
          selectActiveMeasurementValuesWithDisplayUnits,
          selectYAxisTransform,
          selectLineChartHeight,
          selectMeasurementPlotType,
        ],
        (values, transforms, height, plotType) => {
          const data = values[id]?.data;
          if (!data || !data.length) return undefined;
          const minHeight = 0.5;
          const { nominal, limitType, upperLimit, lowerLimit } =
            data[data.length - 1];

          // Handle absolute vs relative limits
          // For absolute limits: use values directly if no nominal, otherwise add nominal
          // For relative limits: add to nominal/relativeTo
          // This matches MeasurementTolerance component behavior
          const isAbsolute =
            limitType && limitType.toLowerCase() === "absolute";
          const relativeTo = nominal ?? 0;

          let upperLimitAbsolute: number;
          let lowerLimitAbsolute: number;

          // We calculate the absolute limits so we can centre the visualisation even when
          // the nominal is outside of the limit range (e.g. double positive tolerance).
          // This matches MeasurementTolerance component behavior
          if (lowerLimit == null && upperLimit == null) {
            lowerLimitAbsolute = relativeTo;
            upperLimitAbsolute = relativeTo;
          } else {
            const minLimit = Math.min(lowerLimit ?? 0, upperLimit ?? 0);
            const maxLimit = Math.max(lowerLimit ?? 0, upperLimit ?? 0);

            lowerLimitAbsolute = isAbsolute
              ? nominal !== undefined
                ? Math.min(nominal, minLimit)
                : minLimit
              : Math.min(relativeTo, relativeTo + minLimit);
            upperLimitAbsolute = isAbsolute
              ? nominal !== undefined
                ? Math.max(nominal, maxLimit)
                : maxLimit
              : Math.max(relativeTo, relativeTo + maxLimit);
          }
          const domainPadding = 0.05;
          const [extentLower, extentUpper] = extent(
            data,
            (x) => x[plotType]
          ) as [number, number];

          const upperScaleValue = Math.max(upperLimitAbsolute!, extentUpper);
          const lowerScaleValue = Math.min(lowerLimitAbsolute!, extentLower);
          const paddingValue =
            (upperScaleValue - lowerScaleValue) * domainPadding;

          const scale = scaleLinear()
            .range([height, minHeight])
            .domain([
              lowerScaleValue - paddingValue,
              upperScaleValue + paddingValue,
            ]);
          const transform = transforms[id];
          if (transform)
            return zoomIdentity
              .translate(0, transform.y)
              .scale(transform.k)
              .rescaleY(scale);
          else return scale;
        }
      )
    : createSelector(
        [
          (state: RootState) => selectTimeSeriesValuesPresentation(state, id),
          selectYAxisTransform,
          selectLineChartHeight,
          selectTimeSeriesLimits,
          selectTimeSeriesTypes,
          selectLatestUnitHints,
          (state: RootState) =>
            state.focusedMachine.timeSeriesDisplayLimits[id],
        ],
        (
          data,
          transforms,
          height,
          limits,
          timeseries,
          hints,
          userDisplayLimits
        ) => {
          if (!data) return undefined;

          let domainExtent = extent(data, (x: any) => x.value) as [
            number,
            number,
          ];
          const minHeight = 0.5;
          const noLimitPadding = 0.05;

          // If user has provided display limits, use those first
          if (
            userDisplayLimits &&
            (userDisplayLimits.lowerLimit !== undefined ||
              userDisplayLimits.upperLimit !== undefined)
          ) {
            const { lowerLimit: userLower, upperLimit: userUpper } =
              userDisplayLimits;
            domainExtent = [
              userLower !== undefined
                ? userLower
                : domainExtent[0] * (1 - noLimitPadding),
              userUpper !== undefined
                ? userUpper
                : domainExtent[1] * (1 + noLimitPadding),
            ];
          } else {
            const seriesType = timeseries.find((t: any) => t.id === id);
            let limitValues = limits[id]?.data.at(-1);

            if (seriesType && limitValues && hints) {
              const displayUnit = getDisplayUnit(
                seriesType,
                hints.value,
                hints?.overrides
              );
              const converter = getUnitConverter(
                seriesType.unitType,
                seriesType.unit,
                displayUnit
              );
              if (typeof converter === "function") {
                limitValues = convertLimits(limitValues, converter);
              }
            }

            if (limitValues) {
              if (
                limitValues.upperDisplayLimit &&
                limitValues.upperDisplayLimit > domainExtent[1]
              )
                domainExtent = [
                  limitValues.lowerDisplayLimit ?? 0,
                  limitValues.upperDisplayLimit,
                ];
              else if (
                limitValues.upperOperatingLimit &&
                limitValues.upperOperatingLimit > domainExtent[1]
              )
                domainExtent = [
                  limitValues.lowerOperatingLimit ?? 0,
                  limitValues.upperOperatingLimit,
                ];
              else if (
                limitValues.upperWarningLimit &&
                limitValues.upperWarningLimit > domainExtent[1]
              )
                domainExtent = [
                  limitValues.lowerWarningLimit ?? 0,
                  limitValues.upperWarningLimit,
                ];
            } else {
              domainExtent = [
                domainExtent[0] * (1 - noLimitPadding),
                domainExtent[1] * (1 + noLimitPadding),
              ];
            }
          }
          const transform = transforms[id];
          const scale = scaleLinear()
            .range([height, minHeight])
            .domain(domainExtent as [number, number])
            .nice();
          if (transform)
            return zoomIdentity
              .translate(0, transform.y)
              .scale(transform.k)
              .rescaleY(scale);
          else return scale;
        }
      );

export function getUnitConverter(
  unitType: string | undefined,
  unit: string | undefined,
  displayUnit: string | undefined
): (_: number) => number {
  const standardisedUnit = units.standardise(unit);
  const standardisedUnitType = units.standardiseUnitType(unitType);
  const standardisedDisplayUnit = units.standardise(displayUnit);

  if (
    !standardisedUnitType ||
    !(standardisedUnitType in units.convert) ||
    !standardisedDisplayUnit
  ) {
    return (x: number) => x;
  }

  const converter =
    units.convert[standardisedUnitType as keyof typeof units.convert][
      standardisedUnit ?? ""
    ]?.to[standardisedDisplayUnit ?? ""];

  if (typeof converter === "function") {
    return converter;
  } else {
    return (x: number) => x;
  }
}

export const selectYDeviationAxisTransform = (s: RootState) =>
  s.visualisations.machineAnalysis.transforms.yDeviation;

export const selectYAxisTransform = (s: RootState) =>
  s.visualisations.machineAnalysis.transforms.yAxes;

export const selectLineChartHeight = (s: RootState) =>
  s.visualisations.machineAnalysis.dimensions.lineChart.height;

export const selectSeriesColours = (s: RootState) =>
  s.visualisations.machineAnalysis.seriesColours;

export const selectFocusedJob = (s: RootState) => s.focusedJob.details;

export const selectFocusedJobSortingDetails = (s: RootState) =>
  s.focusedJob.singleJobTable;

export const selectFocusedJobMeasurementSummarySorting = (s: RootState) =>
  s.focusedJob.measurementSummaryTable;

/** Which table last sorted, and therefore defines the shared measurement row order. */
export const selectMeasurementOrderSource = (s: RootState) =>
  s.focusedJob.measurementOrderSource;

export const selectLatestJob = (s: RootState) => s.latestJob.details;

export const selectJobStatistics = (s: RootState) =>
  s.machineAnalysis.statistics;

/** Resolves each statistic's series colour using the same key (feature name + name) as the detail measurement table. */
export const selectJobStatisticsWithColours = createSelector(
  [selectJobStatistics, selectSeriesColours],
  (statistics, seriesColours) =>
    statistics?.map((row) => ({
      ...row,
      colour: seriesColours[createUniqueMeasurementID(row)] || "#0004",
    }))
);

export const selectJobStatisticsTotalJobRuns = (s: RootState) =>
  s.machineAnalysis.totalJobRuns;

export const selectIsCalculatingJobStatistics = (s: RootState) =>
  s.machineAnalysis.calculatingStatistics;

export const selectIsJobSelectedManually = (s: RootState) =>
  s.global.jobSelectedManuallyFromJobsTimeLine;

/** The details of the focused or latest job */
export const selectFocusedOrLatestJob = createSelector(
  [
    selectLatestJob,
    selectFocusedJob,
    selectFocusedMachineJobs,
    selectIsJobSelectedManually,
    selectCurrentRoute,
  ],
  (latest, main, all, manuallySelected, currentRoute) => {
    // Only machine analysis has the concept of main/latest selected jobs.
    // All other pages should just use the main job.
    if (currentRoute !== "machine analysis") return main;
    return manuallySelected ? main : (latest ?? all.at(-1));
  }
);

export const selectSidebarFilters = (s: RootState) =>
  s.userViewPreference.filters.sidebarFilters;

/** Selects filtered and sorted job type summaries based on active filters and search query */
export const selectFilteredJobTypeSummaries = createSelector(
  [
    selectJobTypeSummaries,
    selectSidebarFilters,
    selectActiveJobFilters,
    selectFocusedOrLatestJob,
    selectFocusedMachineJobs,
    (s: RootState) => s.focusedMachine.jobMetadata,
  ],
  (
    jobTypeSummaries,
    query,
    activeJobFilter,
    mainJob,
    allJobs,
    jobMetadata: MetadataEvent[]
  ) => {
    let filtered = jobTypeSummaries;

    // Apply text search filter
    if (query.jobs) {
      filtered = filtered.filter((j) =>
        j.name.toLowerCase().includes(query.jobs!.toLowerCase())
      );
    }

    // Apply job name filter
    if (activeJobFilter.jobName?.length && activeJobFilter.jobName.length > 0) {
      filtered = filtered.filter((j) =>
        activeJobFilter.jobName!.includes(j.name)
      );
    }

    // Apply verdict filter
    if (activeJobFilter.verdict?.length && activeJobFilter.verdict.length > 0) {
      filtered = filtered.filter((j) => {
        const hasPass =
          j.passes > 0 && activeJobFilter.verdict!.includes("Pass");
        const hasFail =
          j.fails > 0 && activeJobFilter.verdict!.includes("Fail");
        const hasNoVerdict =
          j.noVerdicts > 0 && activeJobFilter.verdict!.includes("No Verdict");
        return hasPass || hasFail || hasNoVerdict;
      });
    }

    // Apply status filter
    if (activeJobFilter.status?.length && activeJobFilter.status.length > 0) {
      filtered = filtered.filter((j) => {
        const hasCompleted =
          j.completed > 0 && activeJobFilter.status!.includes("Completed");
        const hasIncomplete =
          j.incomplete > 0 && activeJobFilter.status!.includes("Incomplete");
        return hasCompleted || hasIncomplete;
      });
    }

    // Apply metadata filter
    if (
      activeJobFilter.metaData &&
      Object.keys(activeJobFilter.metaData).length > 0
    ) {
      const metadataFilterSet = createMetadataFilterSet(
        activeJobFilter.metaData
      );
      const activeJobMetadata = jobMetadata.filter(
        (event) =>
          event?.value !== undefined &&
          event?.value !== null &&
          !!event?.created &&
          metadataFilterSet.has(String(event.value))
      );

      // Filter job types: only keep job types where at least one job of that type has matching metadata
      filtered = filtered.filter((jobTypeSummary) => {
        const jobsOfThisType = allJobs.filter(
          (job) => job.name === jobTypeSummary.name
        );

        // Check if any job has a matching metadata event within its time range
        return jobsOfThisType.some((job) =>
          activeJobMetadata.some((event: any) =>
            isEventInJobTimeRange(event.created, job.start, job.end)
          )
        );
      });
    }

    // Sort: main job to the top, then alphabetically
    return filtered.sort((a, b) => {
      if (mainJob?.name === a.name) return -1;
      if (mainJob?.name === b.name) return 1;
      return a.name.localeCompare(b.name);
    });
  }
);
/** More details of the focused or latest job */
export const selectMainJobMoreDetails = createSelector(
  [(s: RootState) => s.focusedJob, (s: RootState) => s.latestJob],
  (focusedJob, latestJob) => {
    return focusedJob.moreDetails ?? latestJob.moreDetails;
  }
);

/** Related jobs of the focused or latest job */
export const selectRelatedJobs = (s: RootState) => {
  const relatedJobs = s.global.jobSelectedManuallyFromJobsTimeLine
    ? s.focusedJob.relatedJobs
    : s.latestJob.relatedJobs;

  const lastFetchedForRefresh = selectLastFetchedForRefresh(s);
  if (!lastFetchedForRefresh) return relatedJobs;

  // Filter out jobs that started after the last refresh fetch
  const filteredJobs: typeof relatedJobs = {};
  Object.entries(relatedJobs).forEach(([key, job]) => {
    if (job && new Date(job.start) <= lastFetchedForRefresh) {
      filteredJobs[key] = job;
    }
  });
  return filteredJobs;
};

/** Related jobs of the focused or latest job, filtered by the job filters. */
export const selectFilteredRelatedJobs = createSelector(
  [
    selectRelatedJobs,
    selectActiveJobTypes,
    selectActiveJobFilters,
    (s: RootState) => s.focusedMachine.jobMetadata,
  ],
  (relatedJobs, activeJobTypes, jobFilter, jobMetadata) => {
    let filteredArray = filterJobsByJobType(
      Object.values(relatedJobs).filter(notUndefined),
      activeJobTypes
    );
    filteredArray = filterJobsByVerdict(filteredArray, jobFilter.verdict);
    filteredArray = filterJobsByStatus(filteredArray, jobFilter.status);
    filteredArray = filterJobsByMetaData(
      filteredArray,
      jobFilter.metaData,
      jobMetadata
    );
    filteredArray = filterJobsByJobName(filteredArray, jobFilter.jobName);
    return Object.fromEntries(
      filteredArray.map((job) => [job.id, job])
    ) as typeof relatedJobs;
  }
);

/** Current sort column for the job table */
export const selectJobSortColumn = createSelector(
  [(s: RootState) => s.focusedJob ?? s.latestJob],
  (j) => j?.relatedFocusedJob.sortColumn
);

/** Current sort direction for column in the job table */
export const selectJobSortDirection = createSelector(
  [(s: RootState) => s.focusedJob ?? s.latestJob],
  (j) => j?.relatedFocusedJob.sortDirection
);
/**
 * Returns the measurement types of the focused or latest job and its related jobs, sorted based on absolute value of normalisedDeviation.
 * Unique across all focused jobs (relative and main) but sorted on main focused job.
 */
export const selectFocusedJobsSortedMeasurementTypes = createSelector(
  [
    selectFocusedOrLatestJob,
    selectRelatedJobs,
    selectJobSortDirection,
    selectIsOutOfToleranceFilterEnabled,
  ],
  (mainJob, relatedJobs, sortDirection, isOutOfToleranceFilterEnabled) => {
    if (!mainJob || mainJob.type !== "metrology") return [];
    // Create an array of all measurement characteristics types (featureName and name)
    const measurements = [mainJob, ...Object.values(relatedJobs)].flatMap(
      (job) => {
        if (job && job.type === "metrology") {
          const sorted = sortBy(
            (mc) =>
              mc.normalisedDeviation !== undefined
                ? Math.abs(mc.normalisedDeviation)
                : null,
            job.measurementCharacteristics ?? []
          );
          return sortDirection === "desc" ? sorted.reverse() : sorted;
        } else return [];
      }
    );
    const filteredMeasurements = isOutOfToleranceFilterEnabled
      ? measurements.filter((m) => m.verdict === "Fail")
      : measurements;

    // Pick the unique measurement types
    const uniqMeasurements = Object.values(
      filteredMeasurements.reduce(
        (uniq, measurement) =>
          uniq[measurement.featureName + measurement.name]
            ? uniq
            : {
                ...uniq,
                [measurement.featureName + measurement.name]: measurement,
              },
        {} as MappyAbsolute<{
          featureName?: string;
          name: string;
          toleranceType?: string;
          unit?: string;
        }>
      )
    );
    return uniqMeasurements;
  }
);

/**
 * Gets the measurements of a related group of jobs, sorted based on absolute of normalisedDeviation.
 * Unique across all related jobs (relative and main) but sorted on main focused job.
 * @returns a sorted array of measurement metadata objects
 */
export const selectRelatedFocusedJobSortedMeasurementTypes = (
  relatedfocusedJob: undefined | JobPresentation
) =>
  createSelector(
    [selectRelatedJobs, selectJobSortDirection],
    (relatedfocusedJobs, sortDirection) => {
      if (!relatedfocusedJob || relatedfocusedJob.type !== "metrology")
        return [];
      // Create an array of all measurement characteristics types (featureName and name)
      const measurements = [
        relatedfocusedJob,
        ...Object.values(relatedfocusedJobs),
      ].flatMap((job) => {
        if (job && job.type === "metrology") {
          const sorted = sortBy(
            (mc) =>
              mc.normalisedDeviation !== undefined
                ? Math.abs(mc.normalisedDeviation)
                : null,
            job.measurementCharacteristics ?? []
          );
          return sortDirection === "desc" ? sorted.reverse() : sorted;
        } else return [];
      });
      // Pick the unique measurement types
      const uniqMeasurements = Object.values(
        measurements.reduce(
          (uniq, measurement) =>
            uniq[measurement.featureName + measurement.name]
              ? uniq
              : {
                  ...uniq,
                  [measurement.featureName + measurement.name]: measurement,
                },
          {} as MappyAbsolute<{
            featureName?: string;
            name: string;
            toleranceType?: string;
            unit?: string;
          }>
        )
      );
      return uniqMeasurements;
    }
  );

export const selectMainMeasurement = (s: RootState) =>
  s.focusedMachine.mainMeasurementDetail;

export const selectMainTimeSeries = (s: RootState) =>
  s.focusedMachine.mainTimeSeriesDetail;

export const selectTimelineIsZoomed = createSelector(
  [selectTimelineTransform],
  (transform): boolean => transform.k !== zoomIdentity.k
);

export const selectLastFetchedDateTime = createSelector(
  [(s: RootState) => s.focusedMachine.lastFetched],
  (lastFetched): Date | null => (lastFetched ? new Date(lastFetched) : null)
);

export const selectRefreshMode = createSelector(
  [selectIsRollingMode, selectTimelineIsZoomed],
  (isRollingMode, isZoomed): RefreshMode =>
    isRollingMode && !isZoomed ? "refresh" : "locked"
);

export const selectAlertTypeReasons = createSelector(
  [selectAlerts, selectStates, selectStartAndEndDates],
  (selectAlerts, selectStates, dates): AlertTypeReason[] => {
    const errorAlerts = selectAlerts.filter(
      (alert) => alert.level === AlertLevel.error
    );
    const groups = groupBy((alert) => alert.name, errorAlerts);
    const totalRunningInSeconds = calculateUptime(
      selectStates,
      dates[0],
      dates[1]
    );
    const reasons = Object.keys(groups).map((key) => ({
      name: key,
      total: groups[key].length,
    }));
    const total = reasons.reduce(
      (total, nextReason) => total + nextReason.total,
      0
    );
    const sortedReasons = orderBy(["total"], ["desc"], reasons).map(
      (reason) => ({
        ...reason,
        percentage: (reason.total / total) * 100,
      })
    );
    const cumulative = sortedReasons.reduce(
      (a, b) => a.concat((a[a.length - 1] || 0) + b.percentage),
      [] as number[]
    );
    return sortedReasons.map((reason, i) => {
      const date = fixedTimespanFormat(totalRunningInSeconds / reason.total);
      return {
        ...reason,
        id: i + 1,
        cumulativePercentage: cumulative[i],
        meanTimeBeforeFail: date,
      };
    });
  }
);

//  Returns all out of tolerance measurementCharacteristics from mainJob and relatedJobs
export const getFailedMeasurementCharacteristics = createSelector(
  [selectFocusedOrLatestJob, selectRelatedJobs],
  (mainJob, relatedJobs) => {
    return [mainJob, ...Object.values(relatedJobs)].flatMap((job) => {
      if (job && Array.isArray((job as any).measurementCharacteristics)) {
        return (job as any).measurementCharacteristics.filter(
          (mc: any) => mc.verdict === "Fail"
        );
      }
      return [];
    });
  }
);

export const selectMeasurementTypes = createSelector(
  [
    (s: RootState) => s.focusedMachine.measurementDetails,
    getFailedMeasurementCharacteristics,
    selectIsOutOfToleranceFilterEnabled,
  ],
  (measurements, failedMeasurements, isOutOfToleranceFilterEnabled) => {
    const filteredMeasurements = isOutOfToleranceFilterEnabled
      ? Object.values(measurements).filter((m) =>
          failedMeasurements
            .map((me) => `${me.featureName}/${me.name}`)
            .includes(`${m?.featureName}/${m?.name}`)
        )
      : measurements;
    return Object.values(filteredMeasurements).filter(notUndefined);
  }
);

export const selectMeasurementsAsSeries = createSelector(
  [
    selectMeasurementTypes,
    selectFocusedJob,
    selectFocusedJobId,
    (s: RootState) => s.focusedMachine.jobs,
    selectMainMeasurement,
    selectSeriesColours,
    selectLatestUnitHints,
    (s: RootState) =>
      s.focusedMachine.selectedRelatedJobsUniqueMeasurementsCache,
  ],
  (
    types,
    focusedJob,
    focusedJobId,
    jobs,
    focusedMeasurement,
    seriesColours,
    hints,
    selectedRelatedJobsUniqueMeasurementsCache
  ): Series[] => {
    let focusedJobMeasurementTypes: typeof types = [];
    if (focusedJob && focusedJob.type === "metrology") {
      const cachedJobMeasurements =
        selectedRelatedJobsUniqueMeasurementsCache[focusedJob.name];

      if (
        cachedJobMeasurements &&
        cachedJobMeasurements.measurements.length > 0
      ) {
        // Use cached job-specific measurements
        const cachedIds = new Set(
          cachedJobMeasurements.measurements.map((m) =>
            createUniqueMeasurementID(m)
          )
        );
        focusedJobMeasurementTypes = types.filter((item) =>
          cachedIds.has(createUniqueMeasurementID(item))
        );
      } else {
        focusedJobMeasurementTypes = types.filter((item) =>
          focusedJob?.measurementCharacteristics?.some(
            ({ name, featureName }) =>
              featureName === item.featureName && name === item.name
          )
        );
      }
    } else if (focusedJobId) {
      // During a job switch, focusedJob can be reset before new details arrive.
      // Avoid temporarily showing all measurements in this intermediate state.
      const focusedJobSummary = jobs[focusedJobId];
      const cachedJobMeasurements = focusedJobSummary
        ? selectedRelatedJobsUniqueMeasurementsCache[focusedJobSummary.name]
        : undefined;

      if (cachedJobMeasurements?.measurements?.length) {
        const cachedIds = new Set(
          cachedJobMeasurements.measurements.map((m) =>
            createUniqueMeasurementID(m)
          )
        );
        focusedJobMeasurementTypes = types.filter((item) =>
          cachedIds.has(createUniqueMeasurementID(item))
        );
      } else {
        focusedJobMeasurementTypes = [];
      }
    }

    return focusedJobMeasurementTypes.map((measurement) => {
      const id = createUniqueMeasurementID(measurement);
      const colour = (measurement.active && seriesColours[id]) || "#0004";

      return {
        type: "measurement",
        id,
        toleranceType: measurement.toleranceType,
        active: measurement.active,
        colour: colour,
        loading: false,
        main: focusedMeasurement === id,
        name: measurement.name,
        featureGroups: measurement.featureGroups,
        parent: measurement.featureName,
        unit: getDisplayUnit(measurement, hints?.value, hints?.overrides),
      };
    });
  }
);

/** Selects filtered measurements based on sidebar search and popup filters */
export const selectFilteredMeasurementsAsSeries = createSelector(
  [
    selectMeasurementsAsSeries,
    selectSidebarFilters,
    (s: RootState) => s.userViewPreference.filters.measurementFilter.active,
  ],
  (measurementSeries, sidebarFilters, activeMeasurementFilter): Series[] => {
    return measurementSeries.filter((m) => {
      // Text search filter
      if (
        sidebarFilters.measurements &&
        !(m.parent + m.name)
          .toLowerCase()
          .includes(sidebarFilters.measurements.toLowerCase())
      ) {
        return false;
      }

      // Feature name filter (multiple selection)
      if (
        activeMeasurementFilter.featureName &&
        activeMeasurementFilter.featureName.length > 0 &&
        !activeMeasurementFilter.featureName.includes(m.parent || "")
      ) {
        return false;
      }

      // Characteristic name filter (multiple selection)
      if (
        activeMeasurementFilter.characteristicName &&
        activeMeasurementFilter.characteristicName.length > 0 &&
        !activeMeasurementFilter.characteristicName.includes(m.name)
      ) {
        return false;
      }

      return true;
    });
  }
);

/** Selects details of all time series for the focused machine */
export const selectTimeSeriesTypes = createSelector(
  [(s: RootState) => s.focusedMachine.timeSeriesDetails],
  (timeSeries) => Object.values(timeSeries).filter(notUndefined)
);

/** Selects details of active time series for the focused machine */
export const selectActiveTimeSeriesTypes = createSelector(
  [selectTimeSeriesTypes],
  (details) => details.filter(({ active }) => active)
);

export const selectMeasurementPlotType = (s: RootState) =>
  s.userViewPreference.filters.measurementSettings.active.value;

export const selectFocusedJobTab = (s: RootState) =>
  s.focusedMachine.focusedJobTab;

export const selectIsTableViewSectionOpen = (s: RootState) =>
  s.focusedMachine.isTableViewSectionOpen;

export const selectTimeSeriesAsSeries = createSelector(
  [
    selectTimeSeriesTypes,
    selectMainTimeSeries,
    selectSeriesColours,
    selectLatestUnitHints,
    selectFocusedOrLatestJob,
  ],
  (types, focusedTimeSeries, seriesColours, hints): Series[] =>
    types.map((timeSeries): Series => {
      const id = timeSeries.id;
      return {
        type: "sensor",
        id,
        name: timeSeries.name,
        active: timeSeries.active,
        colour: (timeSeries.active && seriesColours[id]) || "#0004",
        loading: false,
        parent: timeSeries.grouping,
        unit: getDisplayUnit(timeSeries, hints?.value, hints?.overrides),
        main: focusedTimeSeries === id,
      };
    })
);

/** Selects filtered time series based on sidebar search and popup filters */
export const selectFilteredTimeSeriesAsSeries = createSelector(
  [
    selectTimeSeriesAsSeries,
    selectSidebarFilters,
    (s: RootState) => s.userViewPreference.filters.timeSeriesFilter.active,
  ],
  (timeSeriesAsSeries, sidebarFilters, timeSeriesFilter): Series[] => {
    return timeSeriesAsSeries.filter((m) => {
      // Text search filter
      if (
        sidebarFilters.timeseries &&
        !(m.parent + m.name)
          .toLowerCase()
          .includes(sidebarFilters.timeseries.toLowerCase())
      ) {
        return false;
      }

      // Grouping name filter (multiple selection)
      if (
        timeSeriesFilter.groupingName?.length &&
        timeSeriesFilter.groupingName.length > 0 &&
        !timeSeriesFilter.groupingName.includes(m.parent || "")
      ) {
        return false;
      }

      // Series name filter (multiple selection)
      if (
        timeSeriesFilter.seriesName?.length &&
        timeSeriesFilter.seriesName.length > 0 &&
        !timeSeriesFilter.seriesName.includes(m.name)
      ) {
        return false;
      }

      return true;
    });
  }
);

/** Selects MeasurementTypeDetails for active measurements on the focused machine */
export const selectActiveMeasurementTypes = createSelector(
  [selectMeasurementTypes],
  (measurements) =>
    Object.values(measurements).filter((measurement) => measurement.active)
);

/** Selects a list of type IDs for active measurements on the focused machine */
export const selectActiveMeasurementTypeIds = createSelector(
  [selectActiveMeasurementTypes],
  (measurements) => measurements.map(createUniqueMeasurementID)
);

const isJobFilterEmpty = (
  jobFilter: ReturnType<typeof selectActiveJobFilters>
) =>
  !jobFilter.jobName?.length &&
  !jobFilter.verdict?.length &&
  !jobFilter.status?.length &&
  !Object.keys(jobFilter.metaData ?? {}).length;

/** Selects the Mappy of raw (unconverted) measurement values for all Jobs on the focused Machine */
const selectMeasurementValues = createSelector(
  [
    (s: RootState) => s.focusedMachine.measurementValues,
    selectActiveMeasurementTypeIds,
    selectFocusedMachineJobTypeFilter,
    selectActiveJobFilters,
    selectFilteredJobs,
  ],
  // Only select measurements for the currently selected jobs
  (measurements, activeIds, jobTypeFilter, jobFilter, jobs) => {
    // Skip if no or all jobs selected
    if (
      isJobFilterEmpty(jobFilter) &&
      (jobTypeFilter.length === 0 || jobTypeFilter.length === jobs.length)
    ) {
      return measurements;
    }

    const current = {} as typeof measurements;
    for (const id in measurements) {
      // Filter to the series whose name and feature name are in the current job
      if (!activeIds.includes(id)) continue;
      // That still includes matching measurements from other jobs, so we have to filter by timebox

      const m = measurements[id]!;
      current[id] = {
        data: m.data.filter((d) =>
          // created date is within a filtered job run
          jobs.some(
            (j) => j.start <= d.created && (!j.end || j.end >= d.created)
          )
        ),
        params: m.params,
      };
    }

    return current;
  }
);

/** Selects details of empty time series for the focused machine */
export const selectActiveTimeSeriesTypesWithoutData = createSelector(
  [
    selectActiveTimeSeriesTypes,
    (s: RootState) => s.focusedMachine.timeSeriesValues,
  ],
  (activeTimeSeriesTypes, values) =>
    activeTimeSeriesTypes.filter(({ id }) => values[id] === undefined)
);

export const selectTimeSeriesLimits = (s: RootState) =>
  s.focusedMachine.timeSeriesLimits;

export const selectTimeSeriesValues = (s: RootState) =>
  s.focusedMachine.timeSeriesValues;

/** Selects time series limits with computed lower and upper limits */
export const selectTimeSeriesLimitsProcessed = createSelector(
  [selectTimeSeriesLimits],
  (timeSerieslimits) => {
    if (!timeSerieslimits) return {};

    return Object.keys(timeSerieslimits).reduce<
      Record<string, { lowerLimit: number; upperLimit: number }>
    >((acc, key) => {
      const limitsData = timeSerieslimits[key];
      const firstDataEntry = limitsData?.data?.[0];

      if (
        firstDataEntry &&
        typeof firstDataEntry.lowerOperatingLimit === "number" &&
        typeof firstDataEntry.upperOperatingLimit === "number"
      ) {
        acc[key] = {
          lowerLimit: firstDataEntry.lowerOperatingLimit,
          upperLimit: firstDataEntry.upperOperatingLimit,
        };
      }
      return acc;
    }, {});
  }
);

/** Selects time series values with computed min, max, and range */
export const selectTimeSeriesMinMaxRange = createSelector(
  [selectTimeSeriesValues, selectTimeSeriesTypes, selectLatestUnitHints],
  (timeSeriesValues, timeSeriesTypes, latestUnitHints) => {
    if (!timeSeriesValues) return {};

    return Object.keys(timeSeriesValues).reduce<
      Record<string, { max: number; min: number; range: number }>
    >((acc, key) => {
      const seriesData = timeSeriesValues[key];
      const seriesType = timeSeriesTypes.find((t) => t.id === key);

      if (seriesData?.data && Array.isArray(seriesData.data) && seriesType) {
        const values: number[] = seriesData.data
          .map((dataPoint) => dataPoint?.[1])
          .filter((value) => value != null)
          .map(Number)
          .filter((value) => !isNaN(value));

        if (values.length > 0) {
          // Find min/max first, then convert only those values
          const rawMax = Math.max(...values);
          const rawMin = Math.min(...values);

          // Get the display unit and converter
          const displayUnit = getDisplayUnit(
            seriesType,
            latestUnitHints?.value,
            latestUnitHints?.overrides
          );

          const converter = getUnitConverter(
            seriesType.unitType,
            seriesType.unit,
            displayUnit
          );

          // Convert only the min/max values
          const max = converter(rawMax);
          const min = converter(rawMin);

          acc[key] = {
            max,
            min,
            range: max - min,
          };
        }
      }
      return acc;
    }, {});
  }
);

/** Selects active time series with all computed data*/
export const selectActiveTimeSeriesSummary = createSelector(
  [
    selectTimeSeriesTypes,
    selectTimeSeriesLimitsProcessed,
    selectTimeSeriesMinMaxRange,
    selectTimeSeriesAsSeries,
    (s: RootState) => s.focusedMachine.timeSeriesSampleRateOverrides,
  ],
  (
    timeSeries,
    limitsProcessed,
    minMaxRange,
    timeSeriesDetails,
    sampleRateOverrides
  ) => {
    if (!timeSeries) return [];

    return timeSeries
      .filter((series) => series.active === true)
      .map((series) => {
        const matchingTimeSeriesDetail = timeSeriesDetails?.find(
          (detail) => detail.id === series.id
        );
        const effectiveSampleRate = matchSampleRate(
          sampleRateOverrides[series.id] ?? series.displayHints?.sampleRate
        );
        return {
          ...series,
          displayHints: {
            ...DEFAULT_DISPLAY_HINTS_FOR_TIMESERIES,
            ...series.displayHints,
            sampleRate: effectiveSampleRate,
          },
          ...(limitsProcessed[series.id] && {
            lowerLimit: limitsProcessed[series.id].lowerLimit,
            upperLimit: limitsProcessed[series.id].upperLimit,
          }),
          ...(minMaxRange[series.id] && {
            max: minMaxRange[series.id].max,
            min: minMaxRange[series.id].min,
            range: minMaxRange[series.id].range,
          }),
          colour: matchingTimeSeriesDetail?.colour ?? "#0004",
        };
      });
  }
);

/** Selects the measurements for the active series on the focused machine, with units converted  */
export const selectActiveMeasurementValuesWithDisplayUnits = createSelector(
  [selectMeasurementValues, selectMeasurementTypes, selectLatestUnitHints],
  (msv, types, hints) => {
    const result: typeof msv = {};
    const keys = Object.keys(msv);
    for (const key of keys) {
      const mc = msv[key]!;
      // Clone all to result
      result[key] = { ...mc };

      const seriesType = types.find(
        (t) => createUniqueMeasurementID(t) === key
      );
      if (!seriesType) continue;
      // Take the last unit hint in the query timebox
      const seriesUnit = units.standardise(seriesType.unit);
      const displayUnit = getDisplayUnit(
        seriesType,
        hints?.value,
        hints?.overrides
      );

      // Can we do a conversion?
      if (displayUnit !== seriesUnit) {
        result[key]!.data = msv[key]!.data.map((m) => {
          return convertMeasurementCharacteristicUnits(
            m,
            seriesUnit,
            displayUnit
          );
        });
      }
    }
    return result;
  }
);

export const selectTimeSeriesValuesWithDisplayUnits = createSelector(
  [selectTimeSeriesValues, selectTimeSeriesTypes, selectLatestUnitHints],
  (tsv, types, hints) => {
    const result: typeof tsv = {};
    const keys = Object.keys(tsv);

    for (const key of keys) {
      const currentTsv = tsv[key]!;

      const seriesType = types.find((t) => t.id === key);
      if (!seriesType) continue;

      const seriesUnit = units.standardise(seriesType.unit);
      const displayUnit = getDisplayUnit(
        seriesType,
        hints?.value,
        hints?.overrides
      );

      // Clone all to result
      result[key] = {
        ...currentTsv,
        data: currentTsv.data.map((d) => {
          return [d[0], d[1], displayUnit];
        }),
      };

      // Can we do a conversion?
      if (displayUnit !== seriesUnit) {
        const precision =
          seriesType.displayHints?.displayPrecision ??
          DefaultTimeSeriesPrecision;
        const converter = getUnitConverter(
          seriesType.unitType,
          seriesType.unit,
          displayUnit
        );
        result[key]!.data = currentTsv.data.map((d) => {
          return [
            d[0],
            tidyNumber(converter(Number(d[1])), precision),
            displayUnit,
          ];
        });
      }
    }
    return result;
  }
);

/** Gets the data for the TimeSeries matching seriesId, with display unit and converted value */
export const selectTimeSeriesValuesPresentation = createSelector(
  [
    selectTimeSeriesValues,
    selectTimeSeriesTypes,
    selectLatestUnitHints,
    (_state, seriesId: GUID) => seriesId,
  ],
  (tsv, types, hints, seriesId) => {
    const currentTsv = tsv[seriesId]!;
    const seriesType = types.find((t) => t.id === seriesId);
    if (!currentTsv || !seriesType)
      return [] as SeriesCompactValuePresentation[];

    const seriesUnit = units.standardise(seriesType.unit);
    const displayUnit = getDisplayUnit(
      seriesType,
      hints?.value,
      hints?.overrides
    );
    const precision =
      seriesType.displayHints?.displayPrecision ?? DefaultTimeSeriesPrecision;

    const converter = getUnitConverter(
      seriesType.unitType,
      seriesUnit,
      displayUnit
    );

    return currentTsv.data.map((d) => {
      const value = converter(Number(d[1]));
      return {
        date: d[0],
        unit: displayUnit,
        value,
        display: tidyNumber(value, precision),
      } as SeriesCompactValuePresentation;
    });
  }
);

export const selectMachineAnalysisView = (s: RootState) =>
  s.userViewPreference.machineAnalysisView;

/**
 * Returns the measurements of the focused or latest job and its related jobs, sorted worst to best.
 *
 * The unfiltered measurement list. Kept separate from
 * `selectFocusedOrLatestJobSortedMeasurements` so the shared measurement row
 * order can be built from every measurement, not just the ones surviving the
 * out-of-tolerance filter. Otherwise toggling that filter would silently
 * reshuffle the summary table.
 */
export const selectFocusedOrLatestJobMeasurements = createSelector(
  [
    selectFocusedOrLatestJob,
    selectRelatedJobs,
    selectLatestUnitHints,
    selectSeriesColours,
    selectMachineAnalysisView,
  ],
  (job, relatedJobs, hints, seriesColours, MachineAnalysisViewType) => {
    if (!job || job.type !== "metrology") return null;

    // For the job tab, the measurement list is based on the view type, i.e., single or multi.
    const requiredMeasurements =
      MachineAnalysisViewType === "Job"
        ? [job]
        : [job, ...Object.values(relatedJobs)];
    // Combine measurements from main job and all related jobs
    const allMeasurements = requiredMeasurements.flatMap((currentJob) => {
      if (
        !currentJob ||
        currentJob.type !== "metrology" ||
        !currentJob.measurementCharacteristics?.length
      ) {
        return [];
      }

      return currentJob.measurementCharacteristics.flatMap((mc) => {
        const seriesUnit = units.standardise(mc.unit);
        const displayUnit = getDisplayUnit(mc, hints?.value, hints?.overrides);
        return convertMeasurementCharacteristicUnits(
          mc,
          seriesUnit,
          displayUnit
        );
      });
    });

    if (allMeasurements.length === 0) return null;

    // Get unique measurements based on featureName + name
    const uniqueMeasurements = new Set<string>();
    const uniqueMeasurementsArray = allMeasurements.filter((measurement) => {
      const key = measurement.featureName + measurement.name;
      if (uniqueMeasurements.has(key)) return false;

      uniqueMeasurements.add(key);
      return true;
    });

    const measurements = uniqueMeasurementsArray
      .slice()
      .sort(sortWorstToBest)
      .map((m) => ({
        ...m,
        colour: seriesColours[createUniqueMeasurementID(m)]
          ? seriesColours[createUniqueMeasurementID(m)]
          : "#0004",
      }));
    return measurements;
  }
);

/** Applies the out-of-tolerance filter to the focused job's measurements. */
export const selectFocusedOrLatestJobSortedMeasurements = createSelector(
  [selectFocusedOrLatestJobMeasurements, selectIsOutOfToleranceFilterEnabled],
  (measurements, isOutOfToleranceFilterEnabled) => {
    if (!measurements) return null;

    return isOutOfToleranceFilterEnabled
      ? measurements.filter((m) => m.verdict === "Fail")
      : measurements;
  }
);

/**
 * The canonical row order shared by the measurement detail and summary tables.
 *
 * AIDEV-NOTE: Each table sorts with its own columns and its own sort function,
 * so only one of them can define the order at a time. `measurementOrderSource`
 * names whichever table the user last sorted; that table's sorted rows are
 * reduced to a key order which *both* tables then render in.
 */
export const selectSharedMeasurementRowOrder = createSelector(
  [
    selectFocusedOrLatestJobMeasurements,
    selectJobStatistics,
    selectFocusedJobSortingDetails,
    selectFocusedJobMeasurementSummarySorting,
    selectMeasurementOrderSource,
  ],
  (measurements, statistics, detailSorting, summarySorting, orderSource) =>
    orderSource === "summary"
      ? toMeasurementRowOrder(
          sortStatistics(
            statistics ?? [],
            summarySorting.sortColumn,
            summarySorting.sortDirection
          )
        )
      : toMeasurementRowOrder(
          sortMeasurements(
            measurements ?? [],
            detailSorting.sortColumn,
            detailSorting.sortDirection
          )
        )
);

/** Selects focused/latest and related jobs on the selected machine, converted to the units of the focused/latest job */
export const selectRelatedJobsWithDisplayUnits = createSelector(
  [
    selectFocusedOrLatestJob,
    selectRelatedJobs,
    selectLatestUnitHints,
    selectIsOutOfToleranceFilterEnabled,
  ],
  (focusedJob, relatedJobs, hints, isOutOfToleranceFilterEnabled) => {
    if (focusedJob?.type !== "metrology") return {};

    const allJobs = {
      ...relatedJobs,
      [focusedJob.id]: focusedJob,
    };
    const convertedJobs: Mappy<
      JobPresentation & { type: "metrology" | "mastering" }
    > = {};

    for (const key in allJobs) {
      // @ts-ignore access object by index
      const job = allJobs[key];
      if (job && job.type === "metrology") {
        //@ts-ignore
        convertedJobs[key] = {
          ...job,
          measurementCharacteristics: (
            job.measurementCharacteristics ?? []
          ).map((mc: MeasurementCharacteristicPresentation) => {
            const seriesUnit = units.standardise(mc.unit);
            const displayUnit = getDisplayUnit(
              mc,
              hints?.value,
              hints?.overrides
            );
            return convertMeasurementCharacteristicUnits(
              mc,
              seriesUnit,
              displayUnit
            );
          }),
        };
      }
    }
    const filteredJobs = isOutOfToleranceFilterEnabled
      ? Object.keys(convertedJobs).reduce(
          (acc, jobId) => {
            const job = convertedJobs[jobId];
            if (job && job.measurementCharacteristics?.length) {
              // Filter measurement characteristics to only include "Fail" verdicts
              const failedMeasurements = job.measurementCharacteristics.filter(
                (mc) => mc.verdict === "Fail"
              );

              // Only include the job if it has failed measurements
              if (failedMeasurements.length > 0) {
                acc[jobId] = {
                  ...job,
                  measurementCharacteristics: failedMeasurements,
                };
              }
            }
            return acc;
          },
          {} as Mappy<JobPresentation & { type: "metrology" | "mastering" }>
        )
      : convertedJobs;
    return filteredJobs;
  }
);

/** Gets the measurement unit to display for a series or measurement type.
 * This is the series' own `unitDisplayHint` property,
 * or the unit from process hints,
 * or the standardised version of its unit.
 * @param seriesType A measurement series or timeseries with name and unit information
 * @param hints The unit type conversions from process hint events, as a {unitType: unitSymbol} dictionary
 * */
export const getDisplayUnit = (
  seriesType: {
    name: string;
    unit?: string;
    unitType?: string;
    unitDisplayHint?: string;
    toleranceType?: string;
    toleranceSubType?: string;
  },
  hints?: Dictionary<string>,
  overrides?: UnitDisplayHintOverride[]
): string => {
  if (seriesType?.unit === undefined || seriesType.unitType === undefined)
    return units.standardise(seriesType?.unit ?? "");
  const unit = units.standardise(seriesType.unit);
  const unitType = units.standardiseUnitType(seriesType.unitType);

  const availableConversions =
    units.convert[unitType as keyof typeof units.convert]?.[unit]?.to ?? {};

  const matchingHintOverride = overrides?.map(
    (p) => p.type === "MeasurementCharacteristic"
  )
    ? overrides?.find(
        (hintOverride) =>
          (!hintOverride.name || hintOverride.name === seriesType.name) &&
          (!hintOverride.toleranceType ||
            stringFunctions.looseMatch(
              hintOverride.toleranceType,
              seriesType.toleranceType
            )) &&
          (!hintOverride.toleranceSubType ||
            stringFunctions.looseMatch(
              hintOverride.toleranceSubType,
              seriesType.toleranceSubType
            )) &&
          unitType?.toLowerCase() ===
            units.standardiseUnitType(hintOverride.unitType)?.toLowerCase()
      )
    : overrides?.find(
        (hintOverride) =>
          hintOverride.name === seriesType.name &&
          unitType?.toLowerCase() ===
            units.standardiseUnitType(hintOverride.unitType)?.toLowerCase()
      );
  if (matchingHintOverride) {
    const overrideUnit = units.standardise(matchingHintOverride.unit);
    // if we can convert to the override unit or the override is the same unit
    if (overrideUnit in availableConversions || overrideUnit === unit) {
      return overrideUnit;
    }
  }

  // Time series hints
  const seriesHintUnit = units.standardise(seriesType.unitDisplayHint);
  if (seriesHintUnit in availableConversions) {
    return seriesHintUnit;
  }
  // Process hints
  if (hints !== undefined) {
    const processHintUnit = units.standardise(hints[unitType ?? ""]);
    if (processHintUnit in availableConversions) {
      return processHintUnit;
    }
  }

  return unit;
};

export const selectStartupState = (s: RootState) => s.startupState;

export const selectFocusedMachineLastFetched = (s: RootState) =>
  s.focusedMachine.lastFetched;

export const selectAwaitingUnitHints = (s: RootState) =>
  s.focusedMachine.awaitingHints;

export const selectActiveFilters = (s: RootState) => s.filter.active;

export const selectAreFiltersModified = createSelector(
  [selectActiveFilters],
  (activeFilter) => {
    const filter = initialFilterState();
    return Object.getOwnPropertyNames(filter).some(
      // @ts-expect-error this is an object it's okay!
      (k) => xor(filter[k], activeFilter[k]).length > 0
    );
  }
);

export const selectYDeviationScale = createSelector(
  [
    selectYDeviationAxisTransform,
    selectLineChartHeight,
    selectActiveMeasurementDetails,
    selectActiveMeasurementValuesWithDisplayUnits,
    selectMainMeasurement,
  ],
  (transform, height, activeTypes, values, mainMeasurementId) => {
    const activeValues = activeTypes
      .map((type) => values[createUniqueMeasurementID(type)]?.data)
      .filter(notUndefined)
      .flatMap((x) => x);
    const mainValues = mainMeasurementId
      ? values[mainMeasurementId]?.data
      : undefined;

    let domain: [number, number] = [-1, 1];
    const domainPadding = 0.05;
    if (mainMeasurementId && mainValues?.length) {
      // when a main measurement exists we want to use it to calculate the domain.
      // domain is min/max of main latest value upper/lower limit and extent of all active measurement values.
      const { nominal, limitType, upperLimit, lowerLimit } =
        mainValues[mainValues.length - 1];

      const [extentLower, extentUpper] = extent(
        activeValues,
        (x) => x.deviation
      ) as [number, number];

      // Handle absolute vs relative limits
      const isAbsolute = limitType && limitType.toLowerCase() === "absolute";
      const relativeTo = nominal ?? 0;

      // Convert limits to absolute values based on limitType
      let absoluteUpperLimitValue: number;
      let absoluteLowerLimitValue: number;

      if (upperLimit !== null && upperLimit !== undefined) {
        absoluteUpperLimitValue = isAbsolute
          ? upperLimit
          : relativeTo + upperLimit;
      } else {
        absoluteUpperLimitValue = relativeTo;
      }

      if (lowerLimit !== null && lowerLimit !== undefined) {
        absoluteLowerLimitValue = isAbsolute
          ? lowerLimit
          : relativeTo + lowerLimit;
      } else {
        absoluteLowerLimitValue = relativeTo;
      }

      // For deviation scale, we need to convert these absolute limits back to deviation values
      // Deviation = actual - nominal, so deviation limits = absolute limits - nominal
      const upperLimitDeviation = absoluteUpperLimitValue - relativeTo;
      const lowerLimitDeviation = absoluteLowerLimitValue - relativeTo;

      // Explicitly check for undefined rather than 'upperLimit ?'
      // as this returns false if upperLimit is 0.
      const absoluteUpperLimit =
        upperLimit !== undefined
          ? Math.max(upperLimitDeviation, extentUpper)
          : extentUpper;
      const absoluteLowerLimit =
        lowerLimit !== undefined
          ? Math.min(lowerLimitDeviation, extentLower)
          : extentLower;

      const paddingValue =
        (absoluteUpperLimit - absoluteLowerLimit) * domainPadding;
      domain = [
        absoluteLowerLimit - paddingValue,
        absoluteUpperLimit + paddingValue,
      ];
    } else if (activeValues.length) {
      // when there isn't a main measurement we just use the extent of the active measurement values.
      const [extentLower, extentUpper] = extent(
        activeValues,
        (x) => x.deviation
      ) as [number, number];
      const paddingValue = (extentUpper - extentLower) * domainPadding;
      domain = [extentLower - paddingValue, extentUpper + paddingValue];
    }
    const scale = scaleLinear().range([height, 0]).domain(domain);
    if (transform)
      return zoomIdentity
        .translate(0, transform.y)
        .scale(transform.k)
        .rescaleY(scale);
    else return scale;
  }
);

export const selectYDeviationUnit = createSelector(
  [selectActiveMeasurementDetails, selectLatestUnitHints],
  (activeTypes, hints) => {
    const uniqueUnits: string[] = activeTypes.reduce<string[]>(
      (units, mType) => {
        if (!mType.unit) return units;

        return [
          ...units,
          getDisplayUnit(mType, hints?.value, hints?.overrides) ?? "",
        ];
      },
      []
    );
    return uniqueUnits.length === 1 ? uniqueUnits[0] : emptyStateText;
  }
);

export const selectActiveSidebarSections = (s: RootState) =>
  s.userViewPreference.activeSidebarSections;

export const selectAssignedLicences = (s: RootState) => s.assignedLicences;

export const selectTotalNumberOfAssignedLicences = createSelector(
  [selectAssignedLicences],
  (assigned) => assigned.length
);

export const selectUnassignedLicences = (s: RootState) => s.unassignedLicences;

export const selectTotalNumberOfUnassignedLicences = createSelector(
  [selectUnassignedLicences],
  (unassigned) => unassigned.length
);

export const selectTotalNumberOfLicences = createSelector(
  [selectAssignedLicences, selectUnassignedLicences],
  (assigned, unassigned) => assigned.length + unassigned.length
);

export const selectlicensedMachines = createSelector(
  [selectMachines, selectAssignedLicences],
  (machines, assignedLicences) => {
    return machines.filter((machine) =>
      assignedLicences.find(({ machineId }) => machineId === machine.id)
    );
  }
);
export const selectLinkedMachines = createSelector(
  [(s: RootState) => s.global.machines],
  (machines) => {
    return machines.filter((machine) => {
      return !!(machine.linkEndpointUri || machine.linkEndpointName);
    });
  }
);

export const selectManageAssetsSelectedNode = (s: RootState) =>
  s.manageAssets.singleSelectedNode;

/** The machine being edited in Manage Assets (API state with no unsaved changes) */
export const selectManageAssetsSelectedMachine = createSelector(
  [selectMachines, selectManageAssetsSelectedNode],
  (machines, id) =>
    id ? machines.find((machine) => machine.id === id) : undefined
);

/** The machine being edited in Manage Assets, with any unsaved changes. This includes the IPC config parts of the form, if available. */
export const selectManageAssetsSelectedMachineWithEdits = createSelector(
  [
    selectManageAssetsSelectedMachine,
    (s: RootState) => s.manageAssets.machineEdits,
  ],
  (machine, edits) => {
    if (!machine) return undefined;
    if (edits?.id !== machine.id) return machine;
    return { ...machine, ...removeUndefinedProps(edits) };
  }
);

/** The IPC configuration for the machine being edited in Manage Assets (API state with no unsaved changes) */
export const selectManageAssetsSelectedMachineConfig = createSelector(
  [
    selectManageAssetsSelectedMachine,
    (s: RootState) =>
      s.manageAssets.machineConfigurations as
        | Mappy<MachineConfiguration>
        | Mappy<GCodeFileDropMachineConfiguration>,
  ],
  (machine, configs) => {
    if (!machine || machine.type !== MachineType.MachineTool) return undefined;
    const conf = configs[machine.id];
    return conf?.dispatchMethod === "FileDrop"
      ? (configs[machine.id] as GCodeFileDropMachineConfiguration)
      : (configs[machine.id] as MachineConfiguration);
  }
);

/** The IPC configuration for the machine being edited in Manage Assets, with any unsaved changes */
export const selectManageAssetsSelectedMachineConfigWithEdits = createSelector(
  [
    selectManageAssetsSelectedMachineConfig,
    (s: RootState) => s.manageAssets.machineConfigEdits,
  ],
  (
    config:
      | MachineConfiguration
      | GCodeFileDropMachineConfiguration
      | undefined,
    edits
  ): MachineConfiguration | GCodeFileDropMachineConfiguration | undefined => {
    // If config is undefined (e.g. immediately after creating a new machine),
    // but edits exist and have a machineId, create a base config and merge edits.
    if (!config) {
      if (edits && edits.machineId && (edits.details || edits.dispatchMethod)) {
        const baseConfig = {
          created: new Date().toISOString(),
          machineId: edits.machineId,
          type: "MachineConfiguration" as const,
          subType: "MachineCommunicationSettings" as const,
          component: "Central IPC Service" as const,
          active: true,
          name: "ControllerSettings" as const,
          dispatchMethod: edits.dispatchMethod,
          details: edits.details,
        };

        if (edits.dispatchMethod === "FileDrop") {
          return baseConfig as GCodeFileDropMachineConfiguration;
        } else {
          return baseConfig as MachineConfiguration;
        }
      }
      return undefined;
    }
    if (edits?.machineId !== config.machineId)
      return config as MachineConfiguration;

    const changes = removeUndefinedProps(edits);
    const editedDispatchMethod = changes?.dispatchMethod;
    const baseDispatchMethod = config.dispatchMethod;
    const effectiveDispatchMethod = editedDispatchMethod ?? baseDispatchMethod;

    if (effectiveDispatchMethod === "FileDrop") {
      const gCodeConfig = config as GCodeFileDropMachineConfiguration;
      const detailsChanges =
        changes?.details as GCodeFileDropMachineConfigurationDetails;
      return {
        ...config,
        ...changes,
        dispatchMethod: "FileDrop" as CommsType,
        details: {
          connectionInfo: {
            gCodeFileSettings: {
              ...gCodeConfig.details?.connectionInfo?.gCodeFileSettings,
              ...detailsChanges?.connectionInfo?.gCodeFileSettings,
            },
            scriptFileSettings: {
              ...gCodeConfig.details?.connectionInfo?.scriptFileSettings,
              ...detailsChanges?.connectionInfo?.scriptFileSettings,
            },
          },
          configurationInfo: {
            ...config.details?.configurationInfo,
            ...changes?.details?.configurationInfo,
          },
          controller: {
            ...config.details?.controller,
            ...changes?.details?.controller,
          },
        },
      } as GCodeFileDropMachineConfiguration;
    } else if (
      effectiveDispatchMethod === "None" ||
      effectiveDispatchMethod === undefined ||
      effectiveDispatchMethod === null
    ) {
      return {
        ...config,
        ...changes,
        dispatchMethod: "None" as CommsType,
        active: false,
        details: undefined,
      } as MachineConfiguration | GCodeFileDropMachineConfiguration;
    } else {
      const conf = {
        ...config,
        ...changes,
        dispatchMethod: "ControllerApi" as CommsType,
        details: {
          controller: {
            ...config.details?.controller,
            ...changes?.details?.controller,
          },
          connectionInfo: {
            ...config.details?.connectionInfo,
            ...changes?.details?.connectionInfo,
          },
          configurationInfo: {
            ...config.details?.configurationInfo,
            ...changes?.details?.configurationInfo,
          },
        },
      } as MachineConfiguration;
      return conf;
    }
  }
);

export const selectManageAssetsMachineFormIsDirty = createSelector(
  [
    selectManageAssetsSelectedMachine,
    selectManageAssetsSelectedMachineWithEdits,
    selectManageAssetsSelectedMachineConfig,
    selectManageAssetsSelectedMachineConfigWithEdits,
  ],
  (
    baseMachine,
    editedMachine,
    baseConfig: MachineConfiguration | GCodeFileDropMachineConfiguration,
    editedConfig: MachineConfiguration | GCodeFileDropMachineConfiguration
  ) => {
    function dispatchMethodMapper(dispatchMethod: string | undefined | null) {
      if (dispatchMethod === undefined || dispatchMethod === null) {
        return "None";
      } else {
        return dispatchMethod;
      }
    }
    // If the current dispatch method is none, then there are no details to compare.
    // dispatchMethod will not exist on the baseConfig if previously saved as 'none',
    // as it exists in the details. Active being false will be equivalent to
    // dispatchMethod being 'none' i.e. no changes. If active is true then there have
    // been changes from the base.
    if (editedConfig?.dispatchMethod === "None") {
      return (
        (editedMachine?.ipAddress || null) !==
          (baseMachine?.ipAddress || null) ||
        editedMachine?.locationId !== baseMachine?.locationId ||
        editedMachine?.make !== baseMachine?.make ||
        editedMachine?.model !== baseMachine?.model ||
        editedMachine?.name !== baseMachine?.name ||
        editedMachine?.serialNumber !== baseMachine?.serialNumber ||
        // here edited is none but base is undefined
        dispatchMethodMapper(editedConfig?.dispatchMethod) !==
          dispatchMethodMapper(baseConfig?.dispatchMethod)
      );
    }

    if (editedConfig?.details && editedConfig?.dispatchMethod === "FileDrop") {
      const edited = editedConfig as GCodeFileDropMachineConfiguration;
      const base = baseConfig as GCodeFileDropMachineConfiguration;
      return (
        // Just compare the fields we can change in the form, so API refresh can't invalidate it
        // IP address needs special comparison; the empty state can be null, undefined or ""
        (editedMachine?.ipAddress || null) !==
          (baseMachine?.ipAddress || null) ||
        editedMachine?.locationId !== baseMachine?.locationId ||
        editedMachine?.make !== baseMachine?.make ||
        editedMachine?.model !== baseMachine?.model ||
        editedMachine?.name !== baseMachine?.name ||
        editedMachine?.serialNumber !== baseMachine?.serialNumber ||
        editedConfig?.dispatchMethod !== baseConfig?.dispatchMethod ||
        // Configuration Info
        (editedConfig &&
          baseConfig &&
          editedConfig.details &&
          baseConfig.details &&
          (edited?.details?.connectionInfo?.gCodeFileSettings?.fileLocation !==
            base?.details?.connectionInfo?.gCodeFileSettings?.fileLocation ||
            edited?.details?.connectionInfo?.gCodeFileSettings?.fileName !==
              base?.details?.connectionInfo?.gCodeFileSettings?.fileName ||
            edited?.details?.connectionInfo?.scriptFileSettings
              ?.fileLocation !==
              base?.details?.connectionInfo?.scriptFileSettings?.fileLocation ||
            edited?.details?.connectionInfo?.scriptFileSettings?.fileName !==
              base?.details?.connectionInfo?.scriptFileSettings?.fileName ||
            edited?.details?.connectionInfo?.scriptFileSettings
              ?.scriptArguments !==
              base?.details?.connectionInfo?.scriptFileSettings
                ?.scriptArguments ||
            edited?.details?.configurationInfo?.headerCommands !==
              base?.details?.configurationInfo?.headerCommands ||
            edited?.details?.configurationInfo?.incrementCommands !==
              base?.details?.configurationInfo?.incrementCommands ||
            edited?.details?.configurationInfo?.absoluteCommands !==
              base.details?.configurationInfo?.absoluteCommands ||
            edited?.details?.configurationInfo?.exitCommands !==
              base?.details?.configurationInfo?.exitCommands ||
            editedConfig?.details?.controller?.units !==
              baseConfig?.details?.controller?.units ||
            editedConfig?.details?.controller?.unitsPrecision !==
              baseConfig?.details?.controller?.unitsPrecision))
      );
    }

    editedConfig = editedConfig as MachineConfiguration;
    baseConfig = baseConfig as MachineConfiguration;
    const connectionAddressChanged =
      (editedConfig?.details?.connectionInfo?.addressType === "Hostname"
        ? editedConfig?.details?.connectionInfo?.hostname !==
          baseConfig?.details?.connectionInfo?.hostname
        : editedConfig?.details?.connectionInfo?.ipAddress !==
          baseConfig?.details?.connectionInfo?.ipAddress) ||
      editedConfig?.details?.connectionInfo?.addressType !==
        baseConfig?.details?.connectionInfo?.addressType;

    return (
      // Just compare the fields we can change in the form, so API refresh can't invalidate it
      // IP address needs special comparison; the empty state can be null, undefined or ""
      (editedMachine?.ipAddress || null) !== (baseMachine?.ipAddress || null) ||
      editedMachine?.locationId !== baseMachine?.locationId ||
      editedMachine?.make !== baseMachine?.make ||
      editedMachine?.model !== baseMachine?.model ||
      editedMachine?.name !== baseMachine?.name ||
      editedMachine?.serialNumber !== baseMachine?.serialNumber ||
      editedConfig?.dispatchMethod !== baseConfig?.dispatchMethod ||
      // Controller
      editedConfig?.details?.controller?.make !==
        baseConfig?.details?.controller?.make ||
      editedConfig?.details?.controller?.model !==
        baseConfig?.details?.controller?.model ||
      editedConfig?.details?.controller?.units !==
        baseConfig?.details?.controller?.units ||
      editedConfig?.details?.controller?.unitsPrecision !==
        baseConfig?.details?.controller?.unitsPrecision ||
      // Connection Info
      connectionAddressChanged ||
      (editedConfig &&
        baseConfig &&
        editedConfig.details &&
        baseConfig.details &&
        (numberOrUndefined(editedConfig.details.connectionInfo?.portNumber) !==
          numberOrUndefined(baseConfig.details.connectionInfo?.portNumber) ||
          numberOrUndefined(
            editedConfig.details.connectionInfo?.connectionTimeoutSecs
          ) !==
            numberOrUndefined(
              baseConfig.details.connectionInfo?.connectionTimeoutSecs
            ) ||
          ((baseConfig.details.controller.make === "Mitsubishi" ||
            baseConfig.details.controller.make === "Citizen") &&
            numberOrUndefined(
              editedConfig.details.connectionInfo?.deviceNumber
            ) !==
              numberOrUndefined(
                baseConfig.details.connectionInfo?.deviceNumber
              )) ||
          (["Siemens", "Citizen"].includes(
            baseConfig.details.controller.make
          ) &&
            numberOrUndefined(
              editedConfig.details.connectionInfo?.connectionChannel
            ) !==
              numberOrUndefined(
                baseConfig.details.connectionInfo?.connectionChannel
              )) ||
          (baseConfig.details.controller.make === "Siemens" &&
            editedConfig.details.configurationInfo?.variableType !==
              baseConfig.details.configurationInfo?.variableType) ||
          (editedConfig.details.configurationInfo?.variableType === "R"
            ? numberOrUndefined(
                editedConfig.details.configurationInfo?.channel
              ) !==
              numberOrUndefined(baseConfig.details.configurationInfo?.channel)
            : editedConfig.details.configurationInfo?.gudArea !==
              baseConfig.details.configurationInfo?.gudArea) ||
          (baseConfig.details.controller.make === "Mazak" &&
            (editedConfig?.details?.connectionInfo?.appName !==
              baseConfig?.details?.connectionInfo?.appName ||
              editedConfig?.details?.connectionInfo?.appPassword !==
                baseConfig?.details?.connectionInfo?.appPassword)) ||
          editedConfig.details.connectionInfo?.useServer !==
            baseConfig.details.connectionInfo?.useServer ||
          // Configuration Info
          numberOrUndefined(
            editedConfig.details.configurationInfo?.syncVariable
          ) !==
            numberOrUndefined(
              baseConfig.details.configurationInfo?.syncVariable
            ) ||
          numberOrUndefined(
            editedConfig.details.configurationInfo?.overrideVariable
          ) !==
            numberOrUndefined(
              baseConfig.details.configurationInfo?.overrideVariable
            ) ||
          numberOrUndefined(
            editedConfig.details.configurationInfo?.alarmVariable
          ) !==
            numberOrUndefined(
              baseConfig.details.configurationInfo?.alarmVariable
            ) ||
          (baseConfig.details.controller.make === "Heidenhain" &&
            (editedConfig.details.configurationInfo?.alarmVariableKey !==
              baseConfig.details.configurationInfo?.alarmVariableKey ||
              editedConfig.details.configurationInfo?.syncVariableKey !==
                baseConfig.details.configurationInfo?.syncVariableKey ||
              editedConfig.details.configurationInfo?.overrideVariableKey !==
                baseConfig.details.configurationInfo?.overrideVariableKey)))) ||
      editedConfig?.details?.connectionInfo?.connectionType !==
        baseConfig?.details?.connectionInfo?.connectionType
    );
  }
);

export const selectManageProvisioningSelectedRequest = createSelector(
  [
    (s: RootState) => s.manageProvisioning.machines,
    (s: RootState) => s.manageProvisioning.selectedRegistrationId,
  ],
  (machines, id) => {
    if (machines && id)
      return machines.find((machine) => machine.registrationId === id);
  }
);

export type LicenceSummary = {
  total: number;
  assigned: number;
  unassigned: number;
  machines: number;
  licences: LicenceDetails[];
};

export const selectLicenceSummary = createSelector(
  [selectMachines, selectAssignedLicences, selectUnassignedLicences],
  (
    machines,
    assignedLicences,
    unassignedLicences
  ): MappyAbsolute<LicenceSummary> => {
    const allLicences = [...assignedLicences, ...unassignedLicences];

    const allLicencesGroupedByType = groupBy(
      (licence) => getMachineTypeAsString(licence.type),
      allLicences
    );

    const uniqueLicenceTypes = [
      ...new Set(
        allLicences.map((licence) => getMachineTypeAsString(licence.type))
      ),
    ];

    const summaries = uniqueLicenceTypes.map((type) => {
      const summary = allLicencesGroupedByType[type];

      const unsortedTypeLicences = unassignedLicences.filter(
        (licence) => getMachineTypeAsString(licence.type) === type
      );
      const licences = orderBy(
        ["transferred", "transferNumber"],
        ["desc", "asc"],
        unsortedTypeLicences
      );
      return summary
        ? {
            type,
            total: summary.length,
            assigned: summary.filter(
              (licence) =>
                licence.machineId !== undefined && licence.machineId !== null
            ).length,
            unassigned: summary.filter(
              (licence) =>
                licence.machineId === undefined || licence.machineId === null
            ).length,
            machines: machines.filter(
              (machine) => getMachineTypeAsString(machine.type) === type
            ).length,
            licences,
          }
        : {
            type,
            total: 0,
            assigned: 0,
            unassigned: 0,
            machines: machines.filter(
              (machine) => getMachineTypeAsString(machine.type) === type
            ).length,
            licences,
          };
    });

    const summaryMap: MappyAbsolute<{
      total: number;
      assigned: number;
      unassigned: number;
      machines: number;
      licences: LicenceDetails[];
    }> = {};

    for (const summary of summaries) {
      summaryMap[summary.type] = summary;
    }

    return summaryMap;
  }
);

export const selectEnableIPC = createSelector(
  [
    (s: RootState) => s.locationEntityPermissions,
    (state: RootState) => state.systemPermissions,
    selectMachines,
    selectLocations,
  ],
  (locationEntityPermissions, sysPermissions, machines, locations) => {
    const licensedIPCMachines = machines.filter(
      (m) =>
        m.isLicensed &&
        m.type === MachineType.Service &&
        /^ipc ?service$/i.test(m.subType || m.model)
    );
    if (!licensedIPCMachines.length) return false;

    if (
      sysPermissions?.includes("dataAdmin") ||
      sysPermissions?.includes("tenantAdmin")
    )
      return true;

    return licensedIPCMachines.some((m) => {
      const loc = locations.find(({ id }) => id === m.locationId);
      return (
        locationEntityPermissions &&
        locationEntityPermissions.some((l) => {
          return (
            (l.entityId === m.locationId && l.permission === "Admin") ||
            (loc?.ancestors &&
              loc?.ancestors.some(
                (s) => s === l.entityId && l.permission === "Admin"
              ))
          );
        })
      );
    });
  }
);

export const selectLastFetchedDate = (type: DataFetchedType) =>
  createSelector(
    [
      selectStartAndEnd,
      (state: RootState) => state.focusedMachine.lastFetched,
      selectStates,
      selectFocusedMachineJobs,
      selectAlerts,
      selectTimeSeriesValues,
      selectMeasurementValues,
      selectProcessActions,
      selectUnitHints,
    ],
    (
      [, to],
      lastFetched,
      states,
      focusedMachineJobs,
      alerts,
      timeSeriesValues,
      measurementSeriesValues,
      processActions,
      hints
    ) => {
      let from = lastFetched ? new Date(lastFetched).toISOString() : to;
      switch (type) {
        case dataFetchedType[0]: // states
          from = states?.at(-1)?.created || from;
          break;
        case dataFetchedType[1]: // jobs (use start, not end – unlike simple events, jobs change after they're created)
          from = focusedMachineJobs?.at(-1)?.start || from;
          break;
        case dataFetchedType[2]: // alerts
          if (alerts.length && alerts.at(-1)?.created !== undefined)
            from = alerts?.at(-1)?.created?.toISOString() || from;
          break;
        case dataFetchedType[3]: // tsv
          if (timeSeriesValues !== undefined) {
            const timeSeriesData = Object.values(timeSeriesValues)[0]?.data;
            from = timeSeriesData?.at(-1)?.[0] || from;
          }
          break;
        case dataFetchedType[4]: //msv
          if (measurementSeriesValues !== undefined) {
            const measurementData = Object.values(measurementSeriesValues)[0]
              ?.data;
            from = measurementData?.at(-1)?.created || from;
          }
          break;
        case dataFetchedType[5]: // processActions
          from = processActions.at(-1)?.created.toISOString() || from;
          break;
        case dataFetchedType[6]: // unitHints
          from = hints.at(-1)?.created || from;
          break;
        case dataFetchedType[7]: // jobMetadata
          break;
        default:
          assertUnreachable(type);
      }
      // Adding a millisecond to the last record's date to avoid duplicate values
      return nextMillisecondISOString(from);
    }
  );

export const selectServerLicenceState = (s: RootState) =>
  s.manageServer.serverLicenceState;

export const isAboutVersionVisible = (state: RootState) =>
  state.aboutPopup.visibility;

function convertMeasurementCharacteristicUnits(
  measurement: MeasurementCharacteristicPresentation,
  seriesUnit: string,
  displayUnit: string
) {
  if (seriesUnit === displayUnit) return measurement;

  const converter = getUnitConverter(
    measurement.unitType,
    seriesUnit,
    displayUnit
  );

  return {
    ...measurement,
    unit: displayUnit,
    actual:
      measurement.actual !== undefined && measurement.actual !== null
        ? converter(measurement.actual)
        : measurement.actual,
    deviation:
      measurement.deviation !== undefined
        ? converter(measurement.deviation)
        : undefined,
    error:
      measurement.error !== undefined
        ? converter(measurement.error)
        : undefined,
    nominal:
      measurement.nominal !== undefined
        ? converter(measurement.nominal)
        : undefined,
    upperWarnLimit:
      measurement.upperWarnLimit !== undefined
        ? converter(measurement.upperWarnLimit)
        : undefined,
    lowerWarnLimit:
      measurement.lowerWarnLimit !== undefined
        ? converter(measurement.lowerWarnLimit)
        : undefined,
    upperLimit:
      measurement.upperLimit !== undefined
        ? converter(measurement.upperLimit)
        : undefined,
    lowerLimit:
      measurement.lowerLimit !== undefined
        ? converter(measurement.lowerLimit)
        : undefined,
  };
}

function convertLimits(
  limitValues: TimeSeriesLimit,
  converter: (val: number) => number
): TimeSeriesLimit {
  const lowerDisplayLimit = limitValues.lowerDisplayLimit
    ? converter(limitValues.lowerDisplayLimit)
    : limitValues.lowerDisplayLimit;
  const upperDisplayLimit = limitValues.upperDisplayLimit
    ? converter(limitValues.upperDisplayLimit)
    : limitValues.upperDisplayLimit;
  const lowerOperatingLimit = limitValues.lowerOperatingLimit
    ? converter(limitValues.lowerOperatingLimit)
    : limitValues.lowerOperatingLimit;
  const upperOperatingLimit = limitValues.upperOperatingLimit
    ? converter(limitValues.upperOperatingLimit)
    : limitValues.upperOperatingLimit;
  const lowerWarningLimit = limitValues.lowerWarningLimit
    ? converter(limitValues.lowerWarningLimit)
    : limitValues.lowerWarningLimit;
  const upperWarningLimit = limitValues.upperWarningLimit
    ? converter(limitValues.upperWarningLimit)
    : limitValues.upperWarningLimit;

  return {
    lowerDisplayLimit: lowerDisplayLimit,
    upperDisplayLimit: upperDisplayLimit,
    lowerOperatingLimit: lowerOperatingLimit,
    upperOperatingLimit: upperOperatingLimit,
    lowerWarningLimit: lowerWarningLimit,
    upperWarningLimit: upperWarningLimit,
    timeSeriesId: limitValues.timeSeriesId,
    created: limitValues.created,
  };
}
